<template>
  <div class="rota-page">

    <!-- ══ LIST VIEW ══════════════════════════════════════════════════════════ -->
    <template v-if="!activeRota">
      <div class="page-header">
        <div>
          <h5 class="page-title">Duty Roster</h5>
          <p class="page-sub">Shift &amp; rota scheduler</p>
        </div>
        <button class="btn-primary" @click="openCreateModal">
          <font-awesome-icon icon="plus" /> New Rota
        </button>
      </div>

      <div class="filter-bar">
        <div class="search-wrap">
          <font-awesome-icon icon="search" class="search-ico" />
          <input v-model="filterSearch" type="text" placeholder="Search rotas…" class="search-input" />
        </div>
        <select v-model="filterStatus" class="filter-sel">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </select>
        <div class="per-page-wrap">
          <label class="per-page-label">Per page:</label>
          <select class="per-page-select" v-model.number="rotaLength" @change="rotaPage = 1; fetchRotas()">
            <option v-for="n in [5,10,20,50,100,200,500,1000]" :key="n" :value="n">{{ n }}</option>
          </select>
        </div>
        <span class="filter-count">{{ rotaPagination ? rotaPagination.total : filteredRotas.length }} rota(s)</span>
      </div>

      <div v-if="listLoading" class="rota-cards">
        <div v-for="n in 4" :key="n" class="sk-card"></div>
      </div>
      <div v-else-if="!filteredRotas.length" class="empty-state">
        <font-awesome-icon icon="calendar-alt" class="empty-icon" />
        <p>No rotas yet — create your first rota to start scheduling.</p>
        <button class="btn-primary" @click="openCreateModal">
          <font-awesome-icon icon="plus" /> New Rota
        </button>
      </div>
      <div v-else class="rota-cards">
        <div v-for="rota in filteredRotas" :key="rota.id" class="rota-card">
          <div class="rota-card-head">
            <div class="rota-icon-wrap"><font-awesome-icon icon="calendar-week" /></div>
            <div class="rota-name-block">
              <p class="rota-name">{{ rota.name }}</p>
              <p class="rota-range mono">{{ fmtDate(rota.date_from) }} – {{ fmtDate(rota.date_to) }}</p>
              <div v-if="rota.location_ids?.length" class="rota-locations">
                <font-awesome-icon icon="map-marker-alt" class="rota-loc-icon" />
                <span>{{ rotaLocationNames(rota.location_ids) }}</span>
              </div>
            </div>
            <span class="badge" :class="rota.status === 'published' ? 'badge--pub' : 'badge--draft'">
              {{ rota.status === 'published' ? 'Published' : 'Draft' }}
            </span>
          </div>
          <div class="rota-stats">
            <div class="rota-stat"><span class="stat-val">{{ rota.entries_count || 0 }}</span><span class="stat-lbl">entries</span></div>
            <div class="rota-stat"><span class="stat-val">{{ daySpan(rota) }}</span><span class="stat-lbl">days</span></div>
          </div>
          <div class="rota-card-foot">
            <button class="btn-open" @click="openRota(rota)"><font-awesome-icon icon="table" /> Open Schedule</button>
            <button class="btn-icon-sm" @click="openEditModal(rota)" title="Edit"><font-awesome-icon icon="pen" /></button>
            <button class="btn-icon-sm btn-icon-sm--danger" @click="confirmDelete(rota)" title="Delete"><font-awesome-icon icon="trash" /></button>
          </div>
        </div>
      </div>

      <!-- Pagination bar -->
      <div class="pagination-bar" v-if="!listLoading && rotaPagination && rotaPagination.last_page > 1">
        <span class="pagination-info">
          Showing {{ (rotaPagination.current_page - 1) * rotaLength + 1 }}–{{ Math.min(rotaPagination.current_page * rotaLength, rotaPagination.total) }} of {{ rotaPagination.total }}
        </span>
        <div class="pagination-controls">
          <button class="pg-btn" :disabled="rotaPagination.current_page === 1" @click="goRotaPage(1)">«</button>
          <button class="pg-btn" :disabled="rotaPagination.current_page === 1" @click="goRotaPage(rotaPagination.current_page - 1)">‹</button>
          <div class="pg-pages">
            <button v-for="p in rotaPageRange" :key="p" class="pg-num" :class="{ 'pg-num--active': p === rotaPagination.current_page }" @click="goRotaPage(p)">{{ p }}</button>
          </div>
          <button class="pg-btn" :disabled="rotaPagination.current_page === rotaPagination.last_page" @click="goRotaPage(rotaPagination.current_page + 1)">›</button>
          <button class="pg-btn" :disabled="rotaPagination.current_page === rotaPagination.last_page" @click="goRotaPage(rotaPagination.last_page)">»</button>
        </div>
      </div>
    </template>

    <!-- ══ SCHEDULE PAGE ══════════════════════════════════════════════════════ -->
    <div v-else class="sched-page">

        <!-- ── Header ─────────────────────────────────────────── -->
        <div class="sched-head">
          <button class="btn-back" @click="closeRota">
            <font-awesome-icon icon="arrow-left" /> Back
          </button>
          <div class="sched-head-info">
            <p class="sched-title">{{ activeRota?.name }}</p>
            <p class="sched-range mono">{{ fmtDate(activeRota?.date_from) }} – {{ fmtDate(activeRota?.date_to) }}</p>
          </div>
          <div class="sched-head-actions">
            <span class="badge" :class="activeRota?.status === 'published' ? 'badge--pub' : 'badge--draft'">
              {{ activeRota?.status === 'published' ? 'Published' : 'Draft' }}
            </span>
            <button class="btn-outline" :disabled="gridSaving" @click="generateRota" title="Auto-fill rota using greedy fairness algorithm">
              <font-awesome-icon :icon="gridSaving && generating ? 'spinner' : 'magic'" :spin="gridSaving && generating" />
              Generate
            </button>
            <div class="export-wrap">
              <button class="btn-outline btn-export" @click="exportOpen = !exportOpen">
                <font-awesome-icon icon="download" /> Export <font-awesome-icon icon="chevron-down" />
              </button>
              <div v-if="exportOpen" class="export-menu">
                <button class="export-item" @click="openExportModal('csv'); exportOpen = false">
                  <font-awesome-icon icon="file-csv" /> Export CSV
                </button>
                <button class="export-item" @click="openExportModal('pdf'); exportOpen = false">
                  <font-awesome-icon icon="file-pdf" /> Export PDF
                </button>
              </div>
            </div>
            <button class="btn-outline" @click="openRollForwardModal">
              <font-awesome-icon icon="forward" /> Roll Forward
            </button>
            <button class="btn-publish" :class="activeRota?.status === 'published' ? 'btn-publish--live' : ''" :disabled="gridSaving" @click="togglePublish">
              <font-awesome-icon :icon="activeRota?.status === 'published' ? 'eye-slash' : 'eye'" />
              {{ activeRota?.status === 'published' ? 'Unpublish' : 'Publish' }}
            </button>
          </div>
        </div>

        <!-- ── Scheduling rules bar ───────────────────────────── -->
        <div class="rules-bar">
          <span class="rules-label">Scheduling rules:</span>
          <label class="rule-field">
            Min rest between shifts
            <input type="number" v-model.number="scheduleRules.minRestHours" min="0" max="48" step="0.5" class="rule-input" /> h
          </label>
          <label class="rule-field">
            Max consecutive days
            <input type="number" v-model.number="scheduleRules.maxConsecutiveDays" min="1" max="14" class="rule-input" />
          </label>
          <span class="rules-hint">Used for conflict detection and auto-generation.</span>
        </div>

        <!-- ── Client filter bar ──────────────────────────────── -->
        <div class="sched-filter-bar">
          <font-awesome-icon icon="building" class="sched-filter-icon" />
          <span class="sched-filter-label">Filter by client:</span>
          <div class="sched-filter-search">
            <search-select
              :key="filterClientKey"
              v-model="filterClient"
              url="/settings/customers/all"
              query-param="query"
              label-key="customer_name"
              value-key="id"
              response-key="data"
              placeholder="All clients"
              search-placeholder="Search clients…"
              :min-chars="1"
              :extra-params="{ is_client: true }"
              @select="onFilterClientSelect"
            />
          </div>
          <button v-if="filterClient" class="sched-filter-clear" @click="clearClientFilter" title="Clear filter">
            <font-awesome-icon icon="times" /> {{ filterClientLabel }}
          </button>
          <div class="sched-filter-type">
            <font-awesome-icon icon="map-marker-alt" class="sched-filter-icon" />
            <span class="sched-filter-label">Location:</span>
            <select class="sched-type-sel" v-model="filterLocation">
              <option value="">All locations</option>
              <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
            </select>
          </div>
          <div class="sched-filter-type">
            <font-awesome-icon icon="tags" class="sched-filter-icon" />
            <span class="sched-filter-label">Shift type:</span>
            <select class="sched-type-sel" v-model="filterShiftType">
              <option value="">All types</option>
              <option v-for="st in shiftTypes" :key="st.id" :value="st.id">{{ st.name }}</option>
            </select>
          </div>
          <div class="sched-filter-emp">
            <font-awesome-icon icon="user" class="sched-filter-icon" />
            <span class="sched-filter-label">Employee:</span>
            <div class="sched-filter-search">
              <search-select
                :key="filterEmployeeKey"
                v-model="filterEmployee"
                url="/payroll/employees/active"
                query-param="search"
                label-key="full_name"
                sublabel-key="employee_no"
                value-key="id"
                response-key="data"
                placeholder="All employees"
                search-placeholder="Search employees…"
                :min-chars="1"
                @select="onFilterEmployeeSelect"
              />
            </div>
            <button v-if="filterEmployee" class="sched-filter-clear" @click="clearEmployeeFilter" title="Clear filter">
              <font-awesome-icon icon="times" /> {{ filterEmployeeLabel }}
            </button>
          </div>
        </div>

        <!-- Loading -->
        <div v-if="gridLoading" class="sched-load">
          <div class="sk-card" style="height:220px"></div>
        </div>

        <div v-else class="sched-body">

          <!-- ── Violations banner ──────────────────────────── -->
          <div class="violations-box" :class="violations.issues.length ? 'violations-box--warn' : 'violations-box--ok'">
            <div class="violations-header" @click="violationsExpanded = !violationsExpanded">
              <template v-if="violations.issues.length">
                <strong>{{ violations.issues.length }} warning{{ violations.issues.length > 1 ? 's' : '' }}</strong>
              </template>
              <template v-else>
                <span>No conflicts detected</span>
              </template>
              <font-awesome-icon :icon="violationsExpanded ? 'chevron-up' : 'chevron-down'" class="violations-chevron" />
            </div>
            <template v-if="violationsExpanded">
              <ul v-if="violations.issues.length">
                <li v-for="(v, i) in violations.issues" :key="i">{{ v }}</li>
              </ul>
              <p v-else class="violations-detail">Rest periods, staffing levels and consecutive-day limits all check out.</p>
            </template>
          </div>

          <!-- ── Grid ──────────────────────────────────────── -->
          <div class="grid-wrap">
            <table class="rota-table">
              <thead>
                <tr>
                  <th class="th-shift"></th>
                  <th
                    v-for="day in rotaDays"
                    :key="day.iso"
                    class="th-day"
                    :class="{ 'th-day--today': day.isToday, 'th-day--weekend': day.isWeekend }"
                  >
                    <div class="th-day-name">{{ day.dayName }}</div>
                    <div class="th-day-date mono">{{ day.short }}</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!filteredGridShifts.length">
                  <td :colspan="rotaDays.length + 1" class="grid-empty">
                    <template v-if="filterClient">No shifts for this client.</template>
                    <template v-else>No active shifts. <router-link :to="$url('hr/shifts')" class="link">Create a shift</router-link> first.</template>
                  </td>
                </tr>
                <tr v-for="shift in filteredGridShifts" :key="shift.id" class="shift-row">
                  <td class="td-shift">
                    <div class="shift-name">{{ shift.shift_type?.name || shift.name }}</div>
                    <div class="shift-time mono">{{ shift.start_time }} – {{ shift.end_time }}</div>
                    <div v-if="shift.client" class="shift-client">
                      <font-awesome-icon icon="building" />
                      {{ shift.client.customer_name }}
                    </div>
                    <button class="btn-bulk-assign" @click="openBulkAssign(shift)" title="Bulk assign staff to this shift">
                      <font-awesome-icon icon="users" /> Bulk Assign
                    </button>
                  </td>
                  <td
                    v-for="day in rotaDays"
                    :key="day.iso"
                    class="td-cell"
                    :class="{
                      'td-cell--today':   day.isToday,
                      'td-cell--weekend': day.isWeekend,
                      'td-cell--past':    day.isPast,
                      'td-cell--short':   violations.understaffedCells.has(shift.id + '|' + day.iso),
                    }"
                  >
                    <!-- N/needed count -->
                    <div class="cell-need" :class="{ 'cell-need--short': violations.understaffedCells.has(shift.id + '|' + day.iso) }">
                      {{ filteredEntries(shift.id, day.iso).length }}<template v-if="shiftNeeded(shift)">/{{ shiftNeeded(shift) }}</template> staff
                    </div>

                    <!-- Employee chips -->
                    <div class="chip-wrap">
                      <span
                        v-for="entry in filteredEntries(shift.id, day.iso)"
                        :key="entry.id"
                        class="chip"
                        :class="{
                          'chip--conflict':       violations.conflictChips.has(shift.id + '|' + day.iso + '|' + entry.employee_id),
                          'chip--assignment':     entry._isAssignment,
                          'chip--past':           day.isPast,
                          'chip--on-leave':       employeeOnLeave(entry.employee_id, day.iso)?.status === 'approved',
                          'chip--leave-pending':  employeeOnLeave(entry.employee_id, day.iso)?.status === 'pending',
                        }"
                        :style="{ background: empColor(entry.employee_id) }"
                        :title="employeeOnLeave(entry.employee_id, day.iso)
                          ? (employeeOnLeave(entry.employee_id, day.iso).status === 'approved'
                              ? `On approved ${employeeOnLeave(entry.employee_id, day.iso).type_name}`
                              : `Pending ${employeeOnLeave(entry.employee_id, day.iso).type_name} request`)
                          : (day.isPast ? 'Cannot edit past shifts' : (entry._isAssignment ? 'Assigned via Shifts page' : ''))"
                        @click="!day.isPast && openEntryMenu(entry, shift, day)"
                      >
                        <span v-if="employeeOnLeave(entry.employee_id, day.iso)" class="chip-leave-dot"
                          :class="{ 'chip-leave-dot--pending': employeeOnLeave(entry.employee_id, day.iso).status === 'pending' }"
                        ></span>
                        <span class="chip-inner">
                          <span class="chip-name">{{ shortName(entry.employee) }}</span>
                          <span v-if="entry.start_time || entry.end_time" class="chip-time">
                            {{ (entry.start_time || shift.start_time) }} – {{ (entry.end_time || shift.end_time) }}
                          </span>
                        </span>
                        <span v-if="!day.isPast" class="chip-x" @click.stop="confirmRemove(entry, shift)" title="Remove">×</span>
                      </span>
                    </div>

                    <!-- Add staff button -->
                    <button
                      v-if="!day.isPast"
                      class="cell-add"
                      :class="{ 'cell-add--full': isShiftFull(shift, day) }"
                      :disabled="isShiftFull(shift, day)"
                      :title="isShiftFull(shift, day) ? 'Shift hours fully covered' : 'Add staff to this shift'"
                      @click="openAddEntry(day, shift)"
                    >
                      <font-awesome-icon :icon="isShiftFull(shift, day) ? 'check' : 'plus'" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- ── Hours ledger ───────────────────────────────── -->
          <div v-if="ledgerRows.length" class="ledger">
            <h3 class="ledger-title">Hours ledger</h3>
            <table class="ledger-table">
              <thead>
                <tr>
                  <th>Staff</th>
                  <th>Contracted</th>
                  <th>Assigned</th>
                  <th>Balance</th>
                  <th>Longest run</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in ledgerRows" :key="row.id">
                  <td>
                    <span class="swatch" :style="{ background: empColor(row.id) }"></span>
                    <span class="ledger-name">{{ row.name }}</span>
                  </td>
                  <td class="mono">{{ row.contractedHours !== null ? row.contractedHours + 'h' : '—' }}</td>
                  <td class="mono">{{ row.assignedHours }}h</td>
                  <td>
                    <div v-if="row.contractedHours !== null" class="gauge">
                      <div class="gauge-track"></div>
                      <div class="gauge-fill" :style="{ width: row.gaugeFillPct + '%', background: row.gaugeColor }"></div>
                      <div class="gauge-marker" :style="{ left: row.gaugeMarkerPct + '%' }"></div>
                    </div>
                    <span v-else class="mono" style="color:var(--ink-faint)">{{ row.assignedHours }}h</span>
                  </td>
                  <td class="mono" :class="{ 'text-warn': row.longestRun > scheduleRules.maxConsecutiveDays }">
                    {{ row.longestRun }}{{ row.longestRun > scheduleRules.maxConsecutiveDays ? ' ⚠' : '' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>
    </div>

    <!-- ══ MODAL: Create / Edit Rota ══════════════════════════════════════════ -->
    <div v-if="rotaModal.show" class="modal-overlay" @click.self="rotaModal.show = false">
      <div class="modal-box">
        <div class="modal-head">
          <p class="modal-title">{{ rotaModal.id ? 'Edit Rota' : 'New Rota' }}</p>
          <button class="btn-icon-sm" @click="rotaModal.show = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <!-- Locations -->
          <div class="field">
            <label class="field-lbl">Locations *</label>
            <div v-if="locations.length" class="multi-sel-box">
              <label
                v-for="loc in locations"
                :key="loc.id"
                class="multi-sel-opt"
                :class="{ 'multi-sel-opt--on': rotaModal.locationIds.includes(loc.id) }"
              >
                <input
                  type="checkbox"
                  :value="loc.id"
                  v-model="rotaModal.locationIds"
                  class="multi-sel-chk"
                />
                <span class="multi-sel-name">{{ loc.name }}</span>
              </label>
            </div>
            <p v-else class="field-hint" style="margin-top:4px">No locations configured yet.</p>
          </div>

          <!-- Name (auto-filled, editable) -->
          <div class="field">
            <label class="field-lbl">Name *</label>
            <input
              v-model="rotaModal.name"
              type="text"
              placeholder="e.g. City Centre – Wk 12 Oct 2026"
              maxlength="100"
              @input="rotaModal.nameManuallyEdited = true"
            />
          </div>

          <div class="field"><label class="field-lbl">Description</label><textarea v-model="rotaModal.description" rows="2" placeholder="Optional notes…" style="resize:vertical;width:100%;padding:7px 9px;border:1px solid var(--line);border-radius:7px;font-size:13px;font-family:Inter,sans-serif;"></textarea></div>
          <div class="field-row">
            <div class="field"><label class="field-lbl">Start Date *</label><input v-model="rotaModal.date_from" type="date" /></div>
            <div class="field"><label class="field-lbl">End Date *</label><input v-model="rotaModal.date_to" type="date" /></div>
          </div>
          <div class="range-btns">
            <button @click="setRange('weekly')">Weekly (7 days)</button>
            <button @click="setRange('fortnightly')">Fortnightly (14 days)</button>
          </div>
        </div>
        <div class="modal-foot">
          <button @click="rotaModal.show = false">Cancel</button>
          <button class="btn-primary" :disabled="modalSaving" @click="saveRota">
            <font-awesome-icon v-if="modalSaving" icon="spinner" spin />
            {{ rotaModal.id ? 'Save Changes' : 'Create Rota' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ══ MODAL: Add Staff to Shift + Day ════════════════════════════════════ -->
    <div v-if="addModal.show" class="modal-overlay" @click.self="addModal.show = false">
      <div class="modal-box">
        <div class="modal-head">
          <p class="modal-title">Add Staff</p>
          <button class="btn-icon-sm" @click="addModal.show = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body modal-body--search">
          <div class="slot-info">
            <font-awesome-icon icon="clock" />
            <strong>{{ addModal.shift?.shift_type?.name || addModal.shift?.name }}</strong>
            <span class="mono">{{ addModal.shift?.start_time }} – {{ addModal.shift?.end_time }}</span>
            <span>·</span>
            <span>{{ addModal.day?.label }}</span>
          </div>

          <!-- Coverage bar -->
          <div class="field">
            <label class="field-lbl">Shift coverage</label>
            <div class="cov-bar-wrap">
              <div class="cov-bar">
                <div
                  v-for="seg in coverageSegments(addModal.shift, addModal.day ? getEntries(addModal.shift?.id, addModal.day?.iso) : [])"
                  :key="seg.empId"
                  class="cov-seg"
                  :style="{ left: seg.leftPct + '%', width: seg.widthPct + '%', background: empColor(seg.empId) }"
                  :title="seg.label"
                ></div>
                <div
                  v-if="addModal.start_time && addModal.end_time"
                  class="cov-seg cov-seg--preview"
                  :style="previewSegStyle(addModal.shift, addModal.start_time, addModal.end_time)"
                ></div>
              </div>
              <div class="cov-legend">
                <span class="cov-legend-item" v-for="seg in coverageSegments(addModal.shift, addModal.day ? getEntries(addModal.shift?.id, addModal.day?.iso) : [])" :key="seg.empId">
                  <span class="cov-dot" :style="{ background: empColor(seg.empId) }"></span>{{ seg.label }}
                </span>
              </div>
              <div class="cov-pct">{{ coveragePct(addModal.shift, addModal.day ? getEntries(addModal.shift?.id, addModal.day?.iso) : [], addModal.start_time, addModal.end_time) }}% covered</div>
            </div>
          </div>

          <div class="field">
            <label class="field-lbl">Employee *</label>
            <search-select :key="addModal.searchKey" v-model="addModal.employee_id" url="/payroll/employees/active" query-param="search" label-key="full_name" sublabel-key="employee_no" value-key="id" response-key="data" placeholder="Search employees…" search-placeholder="Type name or number…" :min-chars="1" @select="onAddEmployeeSelect" />
          </div>

          <!-- Time split -->
          <div class="field">
            <label class="field-lbl">Hours allocation <span class="opt">(leave blank for full shift)</span></label>
            <div class="time-split">
              <div class="time-split-field">
                <span class="time-split-lbl">From</span>
                <input type="time" v-model="addModal.start_time" class="time-split-input" />
              </div>
              <span class="time-split-sep">–</span>
              <div class="time-split-field">
                <span class="time-split-lbl">To</span>
                <input type="time" v-model="addModal.end_time" class="time-split-input" />
              </div>
              <span v-if="addModal.start_time && addModal.end_time" class="time-split-dur">
                {{ calcDur(addModal.start_time, addModal.end_time) }}
              </span>
              <button v-if="addModal.start_time || addModal.end_time" class="time-split-clear" @click="addModal.start_time = ''; addModal.end_time = ''" title="Clear — use full shift">
                <font-awesome-icon icon="times" />
              </button>
            </div>
          </div>

          <div class="field"><label class="field-lbl">Notes</label><input v-model="addModal.notes" type="text" placeholder="Optional note…" maxlength="500" /></div>
        </div>
        <div class="modal-foot">
          <button @click="addModal.show = false">Cancel</button>
          <button class="btn-primary" :disabled="gridSaving || !addModal.employee_id" @click="submitAddEntry">
            <font-awesome-icon v-if="gridSaving" icon="spinner" spin /> Add to Schedule
          </button>
        </div>
      </div>
    </div>

    <!-- ══ MODAL: Bulk Assign ════════════════════════════════════════════════ -->
    <div v-if="bulkModal.show" class="modal-overlay" @click.self="bulkModal.show = false">
      <div class="modal-box bulk-modal">
        <div class="modal-head">
          <div>
            <p class="modal-title">Bulk Assign Staff</p>
            <p class="bulk-shift-tag">
              <font-awesome-icon icon="clock" />
              {{ bulkModal.shift?.shift_type?.name || bulkModal.shift?.name }}
              · {{ bulkModal.shift?.start_time }} – {{ bulkModal.shift?.end_time }}
              <span v-if="bulkModal.shift?.client" class="bulk-client-tag">· {{ bulkModal.shift.client.customer_name }}</span>
            </p>
          </div>
          <button class="btn-icon-sm" @click="bulkModal.show = false"><font-awesome-icon icon="times" /></button>
        </div>

        <div class="modal-body">

          <!-- Employee search-select -->
          <div class="field">
            <label class="field-lbl">Staff member</label>
            <search-select
              :key="bulkModal.searchKey"
              v-model="bulkModal.employee_id"
              url="/payroll/employees/active"
              query-param="search"
              label-key="full_name"
              sublabel-key="employee_no"
              value-key="id"
              response-key="data"
              placeholder="Search employees…"
              search-placeholder="Type name or number…"
              :min-chars="1"
              @select="onBulkEmployeeSelect"
            />
          </div>

          <!-- Day picker -->
          <div class="field">
            <div class="bulk-col-head">
              <label class="field-lbl" style="margin:0">Days</label>
              <div class="bulk-day-shortcuts">
                <button class="bulk-shortcut" @click="selectAllDays">All</button>
                <button class="bulk-shortcut" @click="selectWeekdays">Weekdays</button>
                <button class="bulk-shortcut" @click="bulkModal.selectedDays = []">None</button>
              </div>
            </div>
            <div class="bulk-days">
              <label
                v-for="day in rotaDays"
                :key="day.iso"
                class="bulk-day"
                :class="{
                  'bulk-day--checked':  bulkModal.selectedDays.includes(day.iso),
                  'bulk-day--weekend':  day.isWeekend,
                  'bulk-day--past':     day.isPast,
                }"
              >
                <input type="checkbox" :value="day.iso" v-model="bulkModal.selectedDays" :disabled="day.isPast" />
                <span class="bulk-day-label">
                  <span class="bulk-day-dow">{{ day.label.slice(0, 3) }}</span>
                  <span class="bulk-day-date">{{ day.iso.slice(5) }}</span>
                </span>
              </label>
            </div>
          </div>

        </div>

        <!-- Result summary -->
        <div v-if="bulkModal.result" class="bulk-result" :class="bulkModal.result.skipped?.length ? 'bulk-result--warn' : 'bulk-result--ok'">
          <font-awesome-icon :icon="bulkModal.result.skipped?.length ? 'exclamation-triangle' : 'check-circle'" />
          {{ bulkModal.result.created }} created
          <template v-if="bulkModal.result.skipped?.length">
            · {{ bulkModal.result.skipped.length }} skipped
            <span class="bulk-skip-hint">(duplicates / conflicts)</span>
          </template>
        </div>

        <div class="modal-foot">
          <p class="bulk-preview">
            <strong>{{ bulkModal.selectedDays.length }}</strong> day(s) selected
          </p>
          <button @click="bulkModal.show = false">Close</button>
          <button
            class="btn-primary"
            :disabled="bulkModal.saving || !bulkModal.employee_id || !bulkModal.selectedDays.length"
            @click="submitBulkAssign"
          >
            <font-awesome-icon v-if="bulkModal.saving" icon="spinner" spin />
            Assign
          </button>
        </div>
      </div>
    </div>

    <!-- ══ MODAL: Entry Menu ══════════════════════════════════════════════════ -->
    <div v-if="entryMenu.show" class="modal-overlay" @click.self="entryMenu.show = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-head">
          <p class="modal-title">{{ entryMenu.entry?.employee ? shortName(entryMenu.entry.employee) : 'Employee' }}</p>
          <button class="btn-icon-sm" @click="entryMenu.show = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p class="slot-info slot-info--plain"><strong>{{ entryMenu.shift?.shift_type?.name || entryMenu.shift?.name }}</strong> · {{ entryMenu.day?.label }}</p>
          <p v-if="entryMenu.entry?._isAssignment" class="assign-source-note">Assigned via the Shifts page</p>
          <div class="entry-actions">
            <template v-if="!entryMenu.entry?._isAssignment">
              <button class="entry-btn entry-btn--cover"   @click="openCover"><font-awesome-icon icon="user-shield" /> Cover Shift</button>
              <button class="entry-btn entry-btn--replace" @click="openReplace"><font-awesome-icon icon="exchange-alt" /> Replace Employee</button>
            </template>
            <button class="entry-btn entry-btn--remove" @click="confirmRemove(entryMenu.entry, entryMenu.shift)">
              <font-awesome-icon icon="user-times" />
              {{ entryMenu.entry?._isAssignment ? 'Unassign from Shift' : 'Remove from Rota' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ MODAL: Replace Entry ═══════════════════════════════════════════════ -->
    <div v-if="replaceModal.show" class="modal-overlay" @click.self="replaceModal.show = false">
      <div class="modal-box">
        <div class="modal-head">
          <p class="modal-title">Replace Employee</p>
          <button class="btn-icon-sm" @click="replaceModal.show = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body modal-body--search">
          <p class="replace-note">Replacing <strong>{{ replaceModal.entry ? shortName(replaceModal.entry.employee) : '' }}</strong> on <strong>{{ entryMenu.shift?.shift_type?.name || entryMenu.shift?.name }}</strong> · {{ entryMenu.day?.label }}</p>
          <div class="field">
            <label class="field-lbl">New Employee *</label>
            <search-select :key="replaceModal.searchKey" v-model="replaceModal.new_employee_id" url="/payroll/employees/active" query-param="search" label-key="full_name" sublabel-key="employee_no" value-key="id" response-key="data" placeholder="Search employees…" search-placeholder="Type name or number…" :min-chars="1" @select="onReplaceEmployeeSelect" />
          </div>
          <div class="field">
            <label class="field-lbl">Assign Client <span class="opt">(optional)</span></label>
            <search-select :key="replaceModal.clientSearchKey" v-model="replaceModal.client_id" url="/settings/customers/all" query-param="query" label-key="customer_name" value-key="id" response-key="data" placeholder="Search clients…" search-placeholder="Type client name…" :min-chars="1" :extra-params="{ is_client: true }" @select="onReplaceClientSelect" />
          </div>
          <div class="field"><label class="field-lbl">Notes</label><input v-model="replaceModal.notes" type="text" placeholder="Optional note…" maxlength="500" /></div>
        </div>
        <div class="modal-foot">
          <button @click="replaceModal.show = false">Cancel</button>
          <button class="btn-primary" :disabled="gridSaving || !replaceModal.new_employee_id" @click="submitReplace">
            <font-awesome-icon v-if="gridSaving" icon="spinner" spin /> Confirm Replace
          </button>
        </div>
      </div>
    </div>

    <!-- ══ MODAL: Cover Shift ════════════════════════════════════════════════════ -->
    <div v-if="coverModal.show" class="modal-overlay" @click.self="coverModal.show = false">
      <div class="modal-box">
        <div class="modal-head">
          <p class="modal-title">Cover Shift</p>
          <button class="btn-icon-sm" @click="coverModal.show = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body modal-body--search">

          <!-- Context banner -->
          <div class="cover-context">
            <div class="cover-context-row">
              <span class="cover-unavail-label">Unavailable:</span>
              <span class="cover-chip" :style="{ background: empColor(coverModal.entry?.employee_id) }">
                {{ coverModal.entry ? shortName(coverModal.entry.employee) : '' }}
              </span>
            </div>
            <div class="cover-context-row cover-slot">
              <font-awesome-icon icon="clock" />
              <strong>{{ entryMenu.shift?.shift_type?.name || entryMenu.shift?.name }}</strong>
              <span class="mono">{{ entryMenu.shift?.start_time }} – {{ entryMenu.shift?.end_time }}</span>
              <span>·</span>
              <span>{{ entryMenu.day?.label }}</span>
            </div>
          </div>

          <!-- Cover type -->
          <div class="field">
            <label class="field-lbl">Cover type</label>
            <div class="cover-type-opts">
              <label class="cover-type-opt" :class="{ 'cover-type-opt--active': coverModal.coverType === 'add' }">
                <input type="radio" v-model="coverModal.coverType" value="add" style="display:none" />
                <font-awesome-icon icon="user-plus" />
                <div>
                  <strong>Add alongside</strong>
                  <span>Both employees work the shift</span>
                </div>
              </label>
              <label class="cover-type-opt" :class="{ 'cover-type-opt--active': coverModal.coverType === 'replace' }">
                <input type="radio" v-model="coverModal.coverType" value="replace" style="display:none" />
                <font-awesome-icon icon="exchange-alt" />
                <div>
                  <strong>Replace</strong>
                  <span>Original employee is removed</span>
                </div>
              </label>
            </div>
          </div>

          <!-- Coverage bar -->
          <div class="field">
            <label class="field-lbl">Shift coverage</label>
            <div class="cov-bar-wrap">
              <div class="cov-bar">
                <div
                  v-for="seg in coverageSegments(entryMenu.shift, coverModal.entry ? getEntries(coverModal.entry.shift_id, coverModal.entry.work_date?.slice(0,10)) : [])"
                  :key="seg.empId"
                  class="cov-seg"
                  :style="{ left: seg.leftPct + '%', width: seg.widthPct + '%', background: empColor(seg.empId) }"
                  :title="seg.label"
                ></div>
                <div
                  v-if="coverModal.start_time && coverModal.end_time"
                  class="cov-seg cov-seg--preview"
                  :style="previewSegStyle(entryMenu.shift, coverModal.start_time, coverModal.end_time)"
                ></div>
              </div>
              <div class="cov-legend">
                <span class="cov-legend-item" v-for="seg in coverageSegments(entryMenu.shift, coverModal.entry ? getEntries(coverModal.entry.shift_id, coverModal.entry.work_date?.slice(0,10)) : [])" :key="seg.empId">
                  <span class="cov-dot" :style="{ background: empColor(seg.empId) }"></span>{{ seg.label }}
                </span>
              </div>
              <div class="cov-pct">{{ coveragePct(entryMenu.shift, coverModal.entry ? getEntries(coverModal.entry.shift_id, coverModal.entry.work_date?.slice(0,10)) : [], coverModal.start_time, coverModal.end_time) }}% covered</div>
            </div>
          </div>

          <!-- Time split -->
          <div class="field">
            <label class="field-lbl">Hours allocation <span class="opt">(leave blank for full shift)</span></label>
            <div class="time-split">
              <div class="time-split-field">
                <span class="time-split-lbl">From</span>
                <input type="time" v-model="coverModal.start_time" class="time-split-input" />
              </div>
              <span class="time-split-sep">–</span>
              <div class="time-split-field">
                <span class="time-split-lbl">To</span>
                <input type="time" v-model="coverModal.end_time" class="time-split-input" />
              </div>
              <span v-if="coverModal.start_time && coverModal.end_time" class="time-split-dur">
                {{ calcDur(coverModal.start_time, coverModal.end_time) }}
              </span>
              <button v-if="coverModal.start_time || coverModal.end_time" class="time-split-clear" @click="coverModal.start_time = ''; coverModal.end_time = ''" title="Clear — use full shift">
                <font-awesome-icon icon="times" />
              </button>
            </div>
          </div>

          <div class="field">
            <label class="field-lbl">Cover Employee *</label>
            <search-select
              :key="coverModal.searchKey"
              v-model="coverModal.employee_id"
              url="/payroll/employees/active"
              query-param="search"
              label-key="full_name"
              sublabel-key="employee_no"
              value-key="id"
              response-key="data"
              placeholder="Search employees…"
              search-placeholder="Type name or number…"
              :min-chars="1"
              @select="onCoverEmployeeSelect"
            />
          </div>
          <div class="field">
            <label class="field-lbl">Notes</label>
            <input v-model="coverModal.notes" type="text" placeholder="e.g. Covering for sickness" maxlength="500" />
          </div>
        </div>
        <div class="modal-foot">
          <button @click="coverModal.show = false">Cancel</button>
          <button class="btn-primary" :disabled="gridSaving || !coverModal.employee_id" @click="submitCover">
            <font-awesome-icon v-if="gridSaving" icon="spinner" spin />
            {{ coverModal.coverType === 'replace' ? 'Replace &amp; Confirm Cover' : 'Add Cover' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ══ MODAL: Roll Forward ═════════════════════════════════════════════════ -->
    <div v-if="rollModal.show" class="modal-overlay" @click.self="rollModal.show = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-head">
          <p class="modal-title">Roll Forward</p>
          <button class="btn-icon-sm" @click="rollModal.show = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p class="replace-note">Copy <strong>{{ activeRota?.name }}</strong> forward by:</p>
          <div class="roll-opts">
            <label class="roll-opt" :class="{ 'roll-opt--active': rollModal.period === 'weekly' }">
              <input type="radio" v-model="rollModal.period" value="weekly" style="display:none" />
              <font-awesome-icon icon="calendar-week" />
              <div><strong>1 Week</strong><span class="mono">{{ rollNextDates('weekly') }}</span></div>
            </label>
            <label class="roll-opt" :class="{ 'roll-opt--active': rollModal.period === 'fortnightly' }">
              <input type="radio" v-model="rollModal.period" value="fortnightly" style="display:none" />
              <font-awesome-icon icon="calendar-alt" />
              <div><strong>2 Weeks</strong><span class="mono">{{ rollNextDates('fortnightly') }}</span></div>
            </label>
          </div>
        </div>
        <div class="modal-foot">
          <button @click="rollModal.show = false">Cancel</button>
          <button class="btn-primary" :disabled="gridSaving" @click="submitRollForward">
            <font-awesome-icon v-if="gridSaving" icon="spinner" spin /> Roll Forward
          </button>
        </div>
      </div>
    </div>

    <!-- ══ MODAL: Delete Confirm ═══════════════════════════════════════════════ -->
    <div v-if="deleteModal.show" class="modal-overlay" @click.self="deleteModal.show = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-head">
          <p class="modal-title">Delete Rota</p>
          <button class="btn-icon-sm" @click="deleteModal.show = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p class="replace-note">Delete <strong>{{ deleteModal.rota?.name }}</strong>? All entries will be permanently removed.</p>
        </div>
        <div class="modal-foot">
          <button @click="deleteModal.show = false">Cancel</button>
          <button class="btn-danger-solid" :disabled="modalSaving" @click="submitDelete">
            <font-awesome-icon v-if="modalSaving" icon="spinner" spin /> Delete
          </button>
        </div>
      </div>
    </div>

    <!-- ══ MODAL: Remove Confirm ═══════════════════════════════════════════════ -->
    <div v-if="removeModal.show" class="modal-overlay" @click.self="removeModal.show = false">
      <div class="modal-box modal-box--sm">
        <div class="modal-head">
          <p class="modal-title">{{ removeModal.entry?._isAssignment ? 'Unassign from Shift' : 'Remove from Rota' }}</p>
          <button class="btn-icon-sm" @click="removeModal.show = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p class="replace-note">
            {{ removeModal.entry?._isAssignment ? 'Unassign' : 'Remove' }}
            <strong>{{ removeModal.entry ? shortName(removeModal.entry.employee) : '' }}</strong>
            from <strong>{{ (removeModal.shift?.shift_type?.name || removeModal.shift?.name) || (removeModal.entry?.shift?.shift_type?.name || removeModal.entry?.shift?.name) }}</strong>
            on <strong>{{ removeModal.entry ? fmtDate(removeModal.entry.work_date) : '' }}</strong>?
          </p>
        </div>
        <div class="modal-foot">
          <button @click="removeModal.show = false">Cancel</button>
          <button class="btn-danger-solid" :disabled="gridSaving" @click="submitRemove">
            <font-awesome-icon v-if="gridSaving" icon="spinner" spin />
            {{ removeModal.entry?._isAssignment ? 'Unassign' : 'Remove' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ══ Column picker modal ══ -->
    <transition name="fade">
      <div v-if="showExportModal" class="col-picker-backdrop" @click.self="showExportModal = false">
        <div class="col-picker-modal">
          <div class="col-picker-header">
            <div>
              <h3 class="col-picker-title">Select Columns</h3>
              <p class="col-picker-sub">Choose what to include in your {{ pendingExportFmt.toUpperCase() }} export</p>
            </div>
            <button class="col-picker-close" @click="showExportModal = false"><font-awesome-icon icon="times" /></button>
          </div>
          <div class="col-picker-actions">
            <button class="col-picker-link" @click="selectedExportCols = exportColumnDefs.map(c => c.key)">Select all</button>
            <span class="col-picker-sep">·</span>
            <button class="col-picker-link" @click="selectedExportCols = []">Deselect all</button>
            <span class="col-picker-count">{{ selectedExportCols.length }} of {{ exportColumnDefs.length }} selected</span>
          </div>
          <div class="col-picker-grid">
            <label v-for="col in exportColumnDefs" :key="col.key" class="col-picker-item">
              <input type="checkbox" :value="col.key" v-model="selectedExportCols" class="col-picker-check" />
              <span class="col-picker-label">{{ col.label }}</span>
            </label>
          </div>
          <div class="col-picker-footer">
            <button class="btn-outline btn-secondary" @click="showExportModal = false">Cancel</button>
            <button class="btn-primary-solid" :disabled="selectedExportCols.length === 0 || exportLoading" @click="doExport()">
              <font-awesome-icon :icon="exportLoading ? 'spinner' : pendingExportFmt === 'pdf' ? 'file-pdf' : 'file-csv'" :spin="exportLoading" />
              Export {{ pendingExportFmt.toUpperCase() }}
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import SearchSelect from '../../../../components/SearchSelect.vue';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const PALETTE = [
  '#1F6F5C','#3B5BA5','#8A5A00','#7A4FA0','#B4432A',
  '#2E7D6B','#5C6B7A','#9C4E6B','#4A7A3E','#A05A9C',
];

export default {
  name: 'RotaPage',
  components: { SearchSelect },

  data() {
    return {
      toast: useToast(),

      listLoading:    false,
      rotas:          [],
      rotaLength:     5,
      rotaPage:       1,
      rotaPagination: null,
      filterSearch: '',
      filterStatus: '',
      searchTimer:  null,

      violationsExpanded: false,
      activeRota:    null,
      gridLoading:   false,
      gridSaving:    false,
      generating:    false,
      gridShifts:    [],
      filterClient:        '',
      filterClientLabel:   '',
      filterClientKey:     0,
      filterShiftType:     '',
      filterLocation:      '',
      filterEmployee:      '',
      filterEmployeeLabel: '',
      filterEmployeeKey:   0,
      shiftTypes:          [],
      locations:           [],
      leaveMap:            {},

      // Client-side scheduling rules (used for violation detection and generation)
      scheduleRules: { minRestHours: 11, maxConsecutiveDays: 5 },

      modalSaving: false,

      rotaModal:    { show: false, id: null, name: '', description: '', date_from: '', date_to: '', locationIds: [], nameManuallyEdited: false },
      addModal:     { show: false, day: null, shift: null, employee_id: null, client_id: null, notes: '', start_time: '', end_time: '', searchKey: 0, clientSearchKey: 0 },
      bulkModal:    { show: false, shift: null, employee_id: null, selectedDays: [], searchKey: 0, saving: false, result: null },
      entryMenu:    { show: false, entry: null, shift: null, day: null },
      replaceModal: { show: false, entry: null, new_employee_id: null, client_id: null, notes: '', searchKey: 0, clientSearchKey: 0 },
      coverModal:   { show: false, entry: null, coverType: 'add', employee_id: null, notes: '', start_time: '', end_time: '', searchKey: 0 },
      rollModal:    { show: false, period: 'weekly' },
      deleteModal:  { show: false, rota: null },
      removeModal:  { show: false, entry: null, shift: null },
      exportOpen:         false,
      showExportModal:    false,
      pendingExportFmt:   'csv',
      selectedExportCols: [],
      exportLoading:      false,
      exportColumnDefs: [
        { key: 'day',        label: 'Day',        pdfWidth: 80 },
        { key: 'date',       label: 'Date',       pdfWidth: 90 },
        { key: 'shift',      label: 'Shift',      pdfWidth: 120 },
        { key: 'shift_type', label: 'Shift Type', pdfWidth: 100 },
        { key: 'start',      label: 'Start',      pdfWidth: 75 },
        { key: 'end',        label: 'End',        pdfWidth: 75 },
        { key: 'staff',      label: 'Staff',      pdfWidth: 110 },
        { key: 'client',     label: 'Client',     pdfWidth: 111 },
      ],
    };
  },

  watch: {
    filterSearch() { this.rotaPage = 1; this.debouncedFetchRotas(); },
    filterStatus() { this.rotaPage = 1; this.fetchRotas(); },
    'rotaModal.locationIds': 'autoUpdateRotaName',
    'rotaModal.date_from':   'autoUpdateRotaName',
  },

  computed: {
    rotaPageRange() {
      if (!this.rotaPagination) return [];
      const cur = this.rotaPagination.current_page, last = this.rotaPagination.last_page;
      const pages = [];
      for (let p = Math.max(1, cur - 2); p <= Math.min(last, cur + 2); p++) pages.push(p);
      return pages;
    },

    filteredRotas() {
      return this.rotas;
    },

    filteredGridShifts() {
      return this.gridShifts.filter(s => {
        if (this.filterClient && s.client?.id !== this.filterClient) return false;
        if (this.filterShiftType && s.shift_type_id !== this.filterShiftType) return false;
        if (this.filterLocation && s.location_id !== this.filterLocation) return false;
        return true;
      });
    },


    rotaDays() {
      if (!this.activeRota) return [];
      const days  = [];
      const today = new Date().toISOString().slice(0, 10);
      let cur     = new Date(this.activeRota.date_from);
      const end   = new Date(this.activeRota.date_to);
      while (cur <= end) {
        const iso = cur.toISOString().slice(0, 10);
        const dow = cur.getDay();
        days.push({
          iso,
          isToday:   iso === today,
          isPast:    iso < today,
          isWeekend: dow === 0 || dow === 6,
          dayName:   cur.toLocaleDateString('en-GB', { weekday: 'short' }),
          short:     cur.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
          label:     cur.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' }),
        });
        cur.setDate(cur.getDate() + 1);
      }
      return days;
    },

    // { shift_id: { date_iso: [entry, ...] } }
    // Merges both RotaEntry records (from the rota) and ShiftAssignment records
    // (created via the shifts page fillDay/assign) so both sources appear in the grid.
    entriesMap() {
      const map = {};

      // 1. Rota entries — have a specific work_date
      (this.activeRota?.entries || []).forEach(e => {
        const sid = e.shift_id;
        const d   = e.work_date?.slice(0, 10) ?? e.work_date;
        if (!sid || !d) return;
        if (!map[sid]) map[sid] = {};
        if (!map[sid][d]) map[sid][d] = [];
        map[sid][d].push(e);
      });

      // 2. Shift assignments from the shifts page — have a date range (date_from / date_to)
      //    Only show days that fall within the current rota's period.
      for (const shift of this.gridShifts) {
        const assignments = (shift.assignments || []).filter(a => a.is_active !== false);
        for (const a of assignments) {
          const from = a.date_from?.slice(0, 10);
          const to   = a.date_to?.slice(0, 10);
          if (!from) continue;
          for (const day of this.rotaDays) {
            if (from <= day.iso && (!to || to >= day.iso)) {
              // Skip if already represented by a rota entry for the same employee
              const existing = map[shift.id]?.[day.iso] || [];
              if (existing.some(e => e.employee_id === a.employee_id)) continue;
              if (!map[shift.id]) map[shift.id] = {};
              if (!map[shift.id][day.iso]) map[shift.id][day.iso] = [];
              map[shift.id][day.iso].push({
                id:             a.id,
                shift_id:       shift.id,
                employee_id:    a.employee_id,
                employee:       a.employee,
                work_date:      day.iso,
                start_time:     null,
                end_time:       null,
                _isAssignment:  true,
              });
            }
          }
        }
      }

      return map;
    },

    // Stable employee→palette index (order of first appearance in rota entries)
    empIndexMap() {
      const map = {};
      (this.activeRota?.entries || []).forEach(e => {
        if (!(e.employee_id in map)) map[e.employee_id] = Object.keys(map).length;
      });
      return map;
    },

    // ── Full violation detection (Section 5 of the document) ─────────────────
    violations() {
      const issues             = [];
      const understaffedCells  = new Set();  // "shiftId|date"
      const conflictChips      = new Set();  // "shiftId|date|empId"

      const minRestMins  = this.scheduleRules.minRestHours * 60;
      const maxConsecDays = this.scheduleRules.maxConsecutiveDays;

      // 1. Understaffing ──────────────────────────────────────────────────────
      for (const shift of this.gridShifts) {
        const needed = this.shiftNeeded(shift);
        if (!needed) continue;
        for (const day of this.rotaDays) {
          const count = this.getEntries(shift.id, day.iso).length;
          if (count < needed) {
            understaffedCells.add(shift.id + '|' + day.iso);
            issues.push(`${day.dayName} ${shift.shift_type?.name || shift.name} is short ${needed - count} of ${needed} staff.`);
          }
        }
      }

      // 2. Per-employee: rest breaches, consecutive days, contracted hours ────
      const empTimeline = {};
      for (const entry of (this.activeRota?.entries || [])) {
        if (!entry.shift) continue;
        const date = entry.work_date?.slice(0, 10);
        const di   = this.rotaDays.findIndex(d => d.iso === date);
        if (di === -1) continue;
        const eStart   = entry.start_time || entry.shift.start_time;
        const eEnd     = entry.end_time   || entry.shift.end_time;
        const startAbs = di * 1440 + this.timeToMin(eStart);
        const dur      = this.durationMin(eStart, eEnd);
        const endAbs   = startAbs + dur;
        if (!empTimeline[entry.employee_id]) empTimeline[entry.employee_id] = [];
        empTimeline[entry.employee_id].push({ entry, di, startAbs, endAbs, date, dur });
      }

      for (const empId in empTimeline) {
        const timeline = empTimeline[empId].sort((a, b) => a.startAbs - b.startAbs);
        const emp      = timeline[0]?.entry?.employee;
        const empName  = emp ? this.shortName(emp) : 'A staff member';

        // Rest-period breaches
        for (let i = 1; i < timeline.length; i++) {
          const gap = timeline[i].startAbs - timeline[i - 1].endAbs;
          if (gap < minRestMins) {
            const gapHrs = Math.round((gap / 60) * 10) / 10;
            issues.push(
              `${empName} has only ${gapHrs}h rest between ${timeline[i - 1].entry.shift?.shift_type?.name || timeline[i - 1].entry.shift?.name} (${this.rotaDays[timeline[i-1].di]?.dayName}) and ${timeline[i].entry.shift?.shift_type?.name || timeline[i].entry.shift?.name} (${this.rotaDays[timeline[i].di]?.dayName}).`
            );
            conflictChips.add(`${timeline[i-1].entry.shift_id}|${timeline[i-1].date}|${empId}`);
            conflictChips.add(`${timeline[i].entry.shift_id}|${timeline[i].date}|${empId}`);
          }
        }

        // Consecutive-day breaches
        const days    = [...new Set(timeline.map(t => t.di))].sort((a, b) => a - b);
        const longest = this.longestConsecutiveRun(days);
        if (longest > maxConsecDays) {
          issues.push(`${empName} works ${longest} days in a row, above the ${maxConsecDays}-day limit.`);
        }

        // Contracted-hours breach (if available)
        const contracted = emp?.contracted_hours ?? emp?.contractedHours ?? null;
        if (contracted !== null) {
          const totalMins = timeline.reduce((s, t) => s + t.dur, 0);
          if (totalMins > contracted * 60 + 60) {
            issues.push(`${empName} is assigned ${this.minToHrs(totalMins)}h against a ${contracted}h contract.`);
          }
        }
      }

      // 3. Leave conflicts ─────────────────────────────────────────────────────
      for (const entry of (this.activeRota?.entries || [])) {
        const date  = entry.work_date?.slice(0, 10);
        const leave = this.employeeOnLeave(entry.employee_id, date);
        if (!leave) continue;
        const emp       = entry.employee;
        const empName   = emp ? this.shortName(emp) : 'A staff member';
        const shiftName = entry.shift?.shift_type?.name || entry.shift?.name || 'a shift';
        const dayLabel  = this.rotaDays.find(d => d.iso === date)?.dayName ?? date;
        if (leave.status === 'approved') {
          issues.push(`${empName} is on approved ${leave.type_name} on ${dayLabel} but is assigned to ${shiftName}.`);
          conflictChips.add(`${entry.shift_id}|${date}|${entry.employee_id}`);
        } else {
          issues.push(`${empName} has a pending ${leave.type_name} request on ${dayLabel} — also assigned to ${shiftName}.`);
        }
      }

      return { issues, understaffedCells, conflictChips };
    },

    // ── Hours ledger (Section 7) ──────────────────────────────────────────────
    ledgerRows() {
      const seen = {};
      for (const entry of (this.activeRota?.entries || [])) {
        if (!entry.employee || !entry.shift) continue;
        if (!seen[entry.employee_id]) {
          const contracted = entry.employee.contracted_hours ?? entry.employee.contractedHours ?? null;
          seen[entry.employee_id] = {
            id: entry.employee_id,
            name: `${entry.employee.first_name ?? ''} ${entry.employee.last_name ?? ''}`.trim(),
            contractedHours: contracted,
            assignedMins: 0,
            workedDayIndices: [],
          };
        }
        seen[entry.employee_id].assignedMins += this.durationMin(
          entry.start_time || entry.shift.start_time,
          entry.end_time   || entry.shift.end_time
        );
        const di = this.rotaDays.findIndex(d => d.iso === (entry.work_date?.slice(0, 10) ?? entry.work_date));
        if (di !== -1 && !seen[entry.employee_id].workedDayIndices.includes(di)) {
          seen[entry.employee_id].workedDayIndices.push(di);
        }
      }

      return Object.values(seen).map(row => {
        const assignedHours = this.minToHrs(row.assignedMins);
        const contracted    = row.contractedHours;
        const days          = [...row.workedDayIndices].sort((a, b) => a - b);
        const longestRun    = this.longestConsecutiveRun(days);

        // Gauge (Section 7: teal=within 2h, amber=under, red=over)
        let gaugeFillPct = 0, gaugeMarkerPct = 0, gaugeColor = 'var(--accent)';
        if (contracted !== null) {
          const maxScale    = Math.max(contracted * 1.3, assignedHours * 1.1, 10);
          gaugeFillPct      = Math.min(100, (assignedHours / maxScale) * 100);
          gaugeMarkerPct    = Math.min(100, (contracted   / maxScale) * 100);
          const diff        = assignedHours - contracted;
          gaugeColor        = diff > 2 ? 'var(--alert)' : diff < -2 ? 'var(--amber)' : 'var(--accent)';
        }

        return { ...row, assignedHours, longestRun, gaugeFillPct, gaugeMarkerPct, gaugeColor };
      }).sort((a, b) => b.assignedMins - a.assignedMins);
    },
  },

  mounted() {
    this.fetchRotas();
    this.fetchShiftTypes();
    this.fetchLocations();
  },

  methods: {
    // ── Time helpers ─────────────────────────────────────────────────────────
    timeToMin(t) {
      if (!t) return 0;
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    },
    durationMin(start, end) {
      if (!start || !end) return 0;
      let d = this.timeToMin(end) - this.timeToMin(start);
      if (d <= 0) d += 1440; // overnight
      return d;
    },
    minToHrs(m) {
      return Math.round((m / 60) * 10) / 10;
    },
    longestConsecutiveRun(sortedDays) {
      if (!sortedDays.length) return 0;
      let best = 1, cur = 1;
      for (let i = 1; i < sortedDays.length; i++) {
        cur = sortedDays[i] === sortedDays[i - 1] + 1 ? cur + 1 : 1;
        if (cur > best) best = cur;
      }
      return best;
    },
    shiftNeeded(shift) {
      return shift.min_staff ?? shift.needed ?? shift.staff_required ?? shift.required_employees ?? null;
    },
    isShiftFull(shift, day) {
      const shiftDur = this.durationMin(shift?.start_time, shift?.end_time);
      if (!shiftDur) return false;
      const needed  = this.shiftNeeded(shift) ?? 1;
      const entries = this.getEntries(shift.id, day.iso);
      const covered = entries.reduce(
        (sum, e) => sum + this.durationMin(e.start_time || shift.start_time, e.end_time || shift.end_time),
        0
      );
      return covered >= shiftDur * needed;
    },

    // ── Data ─────────────────────────────────────────────────────────────────
    async fetchShiftTypes() {
      try {
        const { data } = await axios.get('/hr/shift-type/active');
        this.shiftTypes = data.data || [];
      } catch {/* silent */}
    },

    async fetchLocations() {
      try {
        const { data } = await axios.get('/admin/location/active');
        this.locations = data.data || [];
      } catch {/* silent */}
    },

    rotaLocationNames(ids) {
      if (!ids?.length || !this.locations.length) return ids?.join(', ') || '';
      return ids.map(id => this.locations.find(l => l.id === id)?.name).filter(Boolean).join(', ');
    },

    autoUpdateRotaName() {
      if (this.rotaModal.nameManuallyEdited) return;
      const { locationIds, date_from } = this.rotaModal;
      if (!locationIds.length || !date_from) { this.rotaModal.name = ''; return; }
      const names = locationIds
        .map(id => this.locations.find(l => l.id === id)?.name || '')
        .filter(Boolean);
      if (!names.length) return;
      const d = new Date(date_from + 'T00:00:00');
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      this.rotaModal.name = `${names.join(', ')} – Wk ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
    },


    async fetchRotas() {
      this.listLoading = true;
      try {
        const params = { length: this.rotaLength, page: this.rotaPage };
        if (this.filterSearch.trim()) params.search = this.filterSearch.trim();
        if (this.filterStatus)        params.status = this.filterStatus;
        const { data } = await axios.get('/hr/rota', { params });
        this.rotaPagination = data.data;
        this.rotas = data.data?.data || [];
      } catch {
        this.toast.error('Failed to load rotas');
      } finally {
        this.listLoading = false;
      }
    },

    goRotaPage(p) { this.rotaPage = p; this.fetchRotas(); },

    debouncedFetchRotas() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.fetchRotas(), 300);
    },

    async openRota(rota) {
      this.gridLoading = true;
      this.activeRota  = { id: rota.id, name: rota.name, date_from: rota.date_from, date_to: rota.date_to, status: rota.status };
      this.leaveMap    = {};
      try {
        const [rotaRes, shiftsRes] = await Promise.all([
          axios.get(`/hr/rota/${rota.id}`),
          axios.get('/hr/shift', { params: { length: 200, is_active: 1 } }),
        ]);
        this.activeRota      = rotaRes.data.data;
        this.gridShifts      = shiftsRes.data.data?.data || [];
        this.filterLocation  = this.activeRota.location_ids?.length === 1 ? this.activeRota.location_ids[0] : '';
        this.fetchLeaveConflicts(rota.id);
      } catch {
        this.toast.error('Failed to load schedule');
        this.activeRota = null;
      } finally {
        this.gridLoading = false;
      }
    },

    closeRota() {
      this.activeRota      = null;
      this.gridShifts      = [];
      this.leaveMap        = {};
      this.filterLocation  = '';
      this.clearClientFilter();
      this.clearEmployeeFilter();
    },
    onFilterClientSelect(opt) {
      this.filterClient      = opt?.id ?? '';
      this.filterClientLabel = opt?.customer_name ?? '';
    },
    clearClientFilter() {
      this.filterClient      = '';
      this.filterClientLabel = '';
      this.filterClientKey++;
    },
    onFilterEmployeeSelect(opt) {
      this.filterEmployee      = opt?.id ?? '';
      this.filterEmployeeLabel = opt?.full_name ?? '';
    },
    clearEmployeeFilter() {
      this.filterEmployee      = '';
      this.filterEmployeeLabel = '';
      this.filterEmployeeKey++;
    },

    async fetchLeaveConflicts(rotaId) {
      try {
        const { data } = await axios.get(`/hr/rota/${rotaId}/leave-conflicts`);
        const map = {};
        for (const leave of (data.data || [])) {
          const empId = leave.employee_id;
          if (!map[empId]) map[empId] = [];
          map[empId].push({
            from_date: leave.from_date?.slice(0, 10),
            to_date:   leave.to_date?.slice(0, 10),
            status:    leave.status,
            type_name: leave.leave_type?.name ?? 'Leave',
          });
        }
        this.leaveMap = map;
      } catch {
        // Leave data is supplementary — don't surface a toast on failure
      }
    },

    employeeOnLeave(empId, dateIso) {
      const leaves = this.leaveMap[empId] || [];
      return leaves.find(l => l.from_date <= dateIso && l.to_date >= dateIso) ?? null;
    },

    getEntries(shiftId, dateIso) {
      return this.entriesMap[shiftId]?.[dateIso] || [];
    },
    filteredEntries(shiftId, dateIso) {
      const entries = this.getEntries(shiftId, dateIso);
      if (!this.filterEmployee) return entries;
      return entries.filter(e => e.employee_id === this.filterEmployee);
    },

    empColor(employeeId) {
      return PALETTE[(this.empIndexMap[employeeId] ?? 0) % PALETTE.length];
    },

    shortName(emp) {
      if (!emp) return '?';
      const first = emp.first_name ?? '';
      const last  = emp.last_name  ?? '';
      return last ? `${first} ${last[0]}.` : first || '?';
    },

    fmtDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    },

    daySpan(rota) {
      if (!rota.date_from || !rota.date_to) return '—';
      return (new Date(rota.date_to) - new Date(rota.date_from)) / 86400000 + 1;
    },

    rollNextDates(period) {
      if (!this.activeRota) return '';
      const days   = period === 'fortnightly' ? 14 : 7;
      const curEnd = new Date(this.activeRota.date_to);
      const from   = new Date(curEnd); from.setDate(curEnd.getDate() + 1);
      const to     = new Date(curEnd); to.setDate(curEnd.getDate() + days);
      return `${this.fmtDate(from)} – ${this.fmtDate(to)}`;
    },

    // ── Generate rota (Section 4: greedy fairness-ordered allocator) ─────────
    async generateRota() {
      if (!window.confirm(
        'This will clear all existing entries and auto-generate a new schedule using a greedy fairness algorithm. Continue?'
      )) return;

      this.gridSaving = true;
      this.generating = true;
      try {
        // Load all active employees
        const { data: empData } = await axios.get('/payroll/employees/active', { params: { length: 500 } });
        const employees = empData.data?.data || [];

        // Clear existing entries
        for (const entry of [...(this.activeRota.entries || [])]) {
          await axios.delete(`/hr/rota/entries/${entry.id}`);
        }
        this.activeRota.entries = [];

        // Run the greedy allocator
        const assignments = this._runGreedyAllocator(employees);

        // Post each assignment
        let created = 0;
        for (const a of assignments) {
          try {
            await axios.post(`/hr/rota/${this.activeRota.id}/entries`, {
              shift_id:    a.shiftId,
              employee_id: a.employeeId,
              work_date:   a.date,
            });
            created++;
          } catch { /* skip entries the backend rejects */ }
        }

        await this.refreshRota();
        this.toast.success(`Rota generated — ${created} entries created`);
      } catch (err) {
        this.toast.error(err.response?.data?.message || 'Failed to generate rota');
      } finally {
        this.gridSaving = false;
        this.generating = false;
      }
    },

    _runGreedyAllocator(employees) {
      const assignments  = [];
      const assignedMins = {};
      const lastEndAbs   = {};
      const workedDays   = {};

      employees.forEach(e => {
        assignedMins[e.id] = 0;
        lastEndAbs[e.id]   = null;
        workedDays[e.id]   = [];
      });

      // Sort shifts chronologically
      const orderedShifts = [...this.gridShifts].sort(
        (a, b) => this.timeToMin(a.start_time) - this.timeToMin(b.start_time)
      );

      const minRestMins   = this.scheduleRules.minRestHours * 60;
      const maxConsecDays = this.scheduleRules.maxConsecutiveDays;

      for (let di = 0; di < this.rotaDays.length; di++) {
        const day = this.rotaDays[di];

        for (const shift of orderedShifts) {
          const needed   = this.shiftNeeded(shift) ?? 1;
          const dur      = this.durationMin(shift.start_time, shift.end_time);
          const startAbs = di * 1440 + this.timeToMin(shift.start_time);
          const endAbs   = startAbs + dur;

          const bookedToday = new Set(
            assignments.filter(a => a.dayIndex === di).map(a => a.employeeId)
          );

          const contracted = (emp) =>
            (emp.contracted_hours ?? emp.contractedHours ?? 40) * 60;

          // Strict pass: all constraints
          let candidates = employees.filter(e => {
            if (bookedToday.has(e.id)) return false;
            if (lastEndAbs[e.id] !== null && startAbs - lastEndAbs[e.id] < minRestMins) return false;
            // Consecutive days: how long a run would this be?
            const run = this._consecutiveRunEndingAt(workedDays[e.id], di - 1) + 1;
            if (run > maxConsecDays) return false;
            // Hours cap (+1h flex buffer as per document)
            if (assignedMins[e.id] + dur > contracted(e) + 60) return false;
            return true;
          });
          candidates.sort((a, b) => assignedMins[a.id] - assignedMins[b.id]);
          let picked = candidates.slice(0, needed);

          // Relaxation pass (Section 4.2): drop consecutive/hours but keep rest+availability
          if (picked.length < needed) {
            const pickedIds = new Set(picked.map(e => e.id));
            let relaxed = employees.filter(e => {
              if (pickedIds.has(e.id) || bookedToday.has(e.id)) return false;
              if (lastEndAbs[e.id] !== null && startAbs - lastEndAbs[e.id] < minRestMins) return false;
              return true;
            });
            relaxed.sort((a, b) => assignedMins[a.id] - assignedMins[b.id]);
            picked = picked.concat(relaxed.slice(0, needed - picked.length));
          }

          for (const emp of picked) {
            assignments.push({ shiftId: shift.id, employeeId: emp.id, date: day.iso, dayIndex: di });
            assignedMins[emp.id] += dur;
            lastEndAbs[emp.id]    = endAbs;
            workedDays[emp.id].push(di);
          }
        }
      }

      return assignments;
    },

    _consecutiveRunEndingAt(daysArr, day) {
      let run = 0, d = day;
      const set = new Set(daysArr);
      while (set.has(d)) { run++; d--; }
      return run;
    },

    // ── CSV export ───────────────────────────────────────────────────────────
    openExportModal(fmt) {
      this.pendingExportFmt   = fmt;
      this.selectedExportCols = this.exportColumnDefs.map(c => c.key);
      this.showExportModal    = true;
    },
    buildRotaRows() {
      const allRows = [];
      for (const day of this.rotaDays) {
        for (const shift of this.gridShifts) {
          const entries    = this.getEntries(shift.id, day.iso);
          const shiftLabel = shift.shift_type?.name || shift.name || '';
          const shiftType  = shift.shift_type?.name || '';
          if (entries.length === 0) {
            allRows.push({ day: day.label, date: day.iso, shift: shiftLabel, shift_type: shiftType, start: shift.start_time, end: shift.end_time, staff: '', client: shift.client?.customer_name ?? '' });
          } else {
            for (const e of entries) {
              const emp = e.employee;
              allRows.push({ day: day.label, date: day.iso, shift: shiftLabel, shift_type: shiftType, start: shift.start_time, end: shift.end_time, staff: emp ? `${emp.first_name ?? ''} ${emp.last_name ?? ''}`.trim() : '', client: e.client?.customer_name || shift.client?.customer_name || '' });
            }
          }
        }
      }
      return allRows;
    },
    doExport() {
      const cols = this.exportColumnDefs.filter(c => this.selectedExportCols.includes(c.key));
      const rows = this.buildRotaRows();
      const name = this.activeRota?.name ?? 'rota';
      if (this.pendingExportFmt === 'csv') {
        const header = cols.map(c => `"${c.label}"`).join(',');
        const body   = rows.map(r => cols.map(c => `"${String(r[c.key] ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
        const blob = new Blob(['﻿' + header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = name + '.csv'; a.click(); URL.revokeObjectURL(url);
      } else {
        const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
        const usable = 761;
        const totalW = cols.reduce((s, c) => s + c.pdfWidth, 0);
        const scale  = usable / totalW;
        const colStyles = {};
        cols.forEach((c, i) => { colStyles[i] = { cellWidth: c.pdfWidth * scale }; });
        doc.autoTable({
          head: [cols.map(c => c.label)],
          body: rows.map(r => cols.map(c => r[c.key] ?? '')),
          startY: 40, margin: { left: 20, right: 20 }, tableWidth: usable,
          styles: { fontSize: 8, cellPadding: 4, overflow: 'ellipsize' },
          headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold' },
          columnStyles: colStyles,
        });
        doc.save(name + '.pdf');
      }
      this.showExportModal = false;
    },

    // ── Rota CRUD ─────────────────────────────────────────────────────────────
    openCreateModal() {
      this.rotaModal = { show: true, id: null, name: '', description: '', date_from: '', date_to: '', locationIds: [], nameManuallyEdited: false };
    },
    openEditModal(rota) {
      this.rotaModal = {
        show: true, id: rota.id,
        name: rota.name, description: rota.description || '',
        date_from: rota.date_from?.slice(0, 10) ?? '',
        date_to:   rota.date_to?.slice(0, 10)   ?? '',
        locationIds: rota.location_ids ? [...rota.location_ids] : [],
        nameManuallyEdited: true,
      };
    },
    setRange(period) {
      const from = this.rotaModal.date_from ? new Date(this.rotaModal.date_from) : new Date();
      const to   = new Date(from);
      to.setDate(from.getDate() + (period === 'fortnightly' ? 13 : 6));
      this.rotaModal.date_from = from.toISOString().slice(0, 10);
      this.rotaModal.date_to   = to.toISOString().slice(0, 10);
    },
    async saveRota() {
      const m = this.rotaModal;
      if (!m.locationIds.length)       { this.toast.error('At least one location is required'); return; }
      if (!m.name.trim())             { this.toast.error('Name is required'); return; }
      if (!m.date_from || !m.date_to) { this.toast.error('Date range is required'); return; }
      this.modalSaving = true;
      try {
        const payload = { name: m.name, description: m.description, location_ids: m.locationIds, date_from: m.date_from, date_to: m.date_to };
        if (m.id) { await axios.put(`/hr/rota/${m.id}`, payload); this.toast.success('Rota updated'); }
        else       { await axios.post('/hr/rota', payload);         this.toast.success('Rota created'); }
        this.rotaModal.show = false;
        this.fetchRotas();
      } catch (err) {
        this.toast.error(err.response?.data?.message || 'Failed to save rota');
      } finally { this.modalSaving = false; }
    },
    confirmDelete(rota) { this.deleteModal = { show: true, rota }; },
    async submitDelete() {
      this.modalSaving = true;
      try {
        await axios.delete(`/hr/rota/${this.deleteModal.rota.id}`);
        this.toast.success('Rota deleted');
        this.deleteModal.show = false;
        this.fetchRotas();
      } catch (err) {
        this.toast.error(err.response?.data?.message || 'Failed to delete');
      } finally { this.modalSaving = false; }
    },

    // ── Publish ───────────────────────────────────────────────────────────────
    async togglePublish() {
      this.gridSaving = true;
      try {
        const { data } = await axios.put(`/hr/rota/${this.activeRota.id}/publish`);
        this.activeRota.status = data.data?.status ?? (this.activeRota.status === 'published' ? 'draft' : 'published');
        this.toast.success(data.message || 'Status updated');
        const idx = this.rotas.findIndex(r => r.id === this.activeRota.id);
        if (idx !== -1) this.rotas[idx].status = this.activeRota.status;
      } catch (err) {
        this.toast.error(err.response?.data?.message || 'Failed to update status');
      } finally { this.gridSaving = false; }
    },

    // ── Roll Forward ──────────────────────────────────────────────────────────
    openRollForwardModal() { this.rollModal = { show: true, period: 'weekly' }; },
    async submitRollForward() {
      this.gridSaving = true;
      try {
        const { data } = await axios.post(`/hr/rota/${this.activeRota.id}/roll-forward`, { period: this.rollModal.period });
        this.toast.success('Rota rolled forward');
        this.rollModal.show = false;
        this.fetchRotas();
        await this.openRota(data.data);
      } catch (err) {
        this.toast.error(err.response?.data?.message || 'Failed to roll forward');
      } finally { this.gridSaving = false; }
    },

    // ── Add Entry ─────────────────────────────────────────────────────────────
    openAddEntry(day, shift) {
      const t = Date.now();
      this.addModal = { show: true, day, shift, employee_id: null, client_id: null, notes: '', start_time: '', end_time: '', searchKey: t, clientSearchKey: t + 1 };
    },

    // ── Bulk Assign ───────────────────────────────────────────────────────────
    openBulkAssign(shift) {
      this.bulkModal = { show: true, shift, employee_id: null, selectedDays: [], searchKey: Date.now(), saving: false, result: null };
    },

    onBulkEmployeeSelect(opt) { this.bulkModal.employee_id = opt?.id ?? null; },

    selectAllDays() {
      this.bulkModal.selectedDays = this.rotaDays.filter(d => !d.isPast).map(d => d.iso);
    },

    selectWeekdays() {
      this.bulkModal.selectedDays = this.rotaDays.filter(d => !d.isPast && !d.isWeekend).map(d => d.iso);
    },

    async submitBulkAssign() {
      const m = this.bulkModal;
      if (!m.employee_id || !m.selectedDays.length) return;

      // Client-side pre-check: skip days where employee already has an overlapping entry
      const shift = m.shift;
      const toMin = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
      const overlaps = (s1, e1, s2, e2) => {
        let a1 = toMin(s1), a2 = toMin(e1), b1 = toMin(s2), b2 = toMin(e2);
        if (a2 <= a1) a2 += 1440;
        if (b2 <= b1) b2 += 1440;
        return a1 < b2 && b1 < a2;
      };
      const newStart = shift.start_time;
      const newEnd   = shift.end_time;

      const conflictDays = m.selectedDays.filter(iso => {
        return (this.activeRota?.entries || []).some(e => {
          if (e.employee_id !== m.employee_id) return false;
          if (e.work_date?.slice(0, 10) !== iso) return false;
          if (e.shift_id === shift.id) return false; // duplicate check handled separately
          const es = e.start_time || e.shift?.start_time;
          const ee = e.end_time   || e.shift?.end_time;
          if (!es || !ee) return false;
          return overlaps(newStart, newEnd, es, ee);
        });
      });

      if (conflictDays.length) {
        this.toast.warning(`${conflictDays.length} day(s) skipped — employee already has an overlapping shift on those dates`);
        m.selectedDays = m.selectedDays.filter(d => !conflictDays.includes(d));
        if (!m.selectedDays.length) return;
      }

      m.saving = true;
      m.result = null;
      try {
        const assignments = m.selectedDays.map(work_date => ({ employee_id: m.employee_id, work_date }));
        const { data } = await axios.post(`/hr/rota/${this.activeRota.id}/entries/bulk`, {
          shift_id: m.shift.id,
          assignments,
        });
        m.result = data.data;
        await this.refreshRota();
        if (data.data?.created > 0) {
          this.toast.success(data.message);
        } else {
          this.toast.warning(data.message || 'No new assignments created');
        }
      } catch (err) {
        this.toast.error(err.response?.data?.message || 'Bulk assign failed');
      } finally {
        m.saving = false;
      }
    },
    onAddEmployeeSelect(opt) { this.addModal.employee_id = opt?.id ?? null; },
    onAddClientSelect(opt)   { this.addModal.client_id   = opt?.id ?? null; },
    async refreshRota() {
      try {
        const [rotaRes, shiftsRes] = await Promise.all([
          axios.get(`/hr/rota/${this.activeRota.id}`),
          axios.get('/hr/shift', { params: { length: 200, is_active: 1 } }),
        ]);
        this.activeRota = rotaRes.data.data;
        this.gridShifts = shiftsRes.data.data?.data || [];
      } catch { /* silent – grid will show stale data but won't crash */ }
    },

    async submitAddEntry() {
      if (!this.addModal.employee_id) { this.toast.error('Select an employee'); return; }
      if (this.isShiftFull(this.addModal.shift, this.addModal.day)) {
        this.toast.error(`Shift hours are fully covered for ${this.addModal.day.label}`);
        return;
      }

      // Client-conflict check: staff cannot be on two different clients in the same timeframe
      const newClient = this.addModal.shift.client_id;
      if (newClient) {
        const newStart = this.addModal.start_time || this.addModal.shift.start_time;
        const newEnd   = this.addModal.end_time   || this.addModal.shift.end_time;
        const toMin    = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
        let ns = toMin(newStart), ne = toMin(newEnd);
        if (ne <= ns) ne += 1440;

        const conflict = (this.activeRota?.entries || []).find(e => {
          if (e.employee_id !== this.addModal.employee_id) return false;
          if (e.work_date?.slice(0, 10) !== this.addModal.day.iso) return false;
          const eClient = e.client_id || e.shift?.client_id;
          if (!eClient || eClient === newClient) return false;
          const es = e.start_time || e.shift?.start_time;
          const ee = e.end_time   || e.shift?.end_time;
          if (!es || !ee) return false;
          let s2 = toMin(es), e2 = toMin(ee);
          if (e2 <= s2) e2 += 1440;
          return ns < e2 && s2 < ne;
        });

        if (conflict) {
          const cName = conflict.client?.customer_name || conflict.shift?.client?.customer_name || 'another client';
          this.toast.error(`Staff already has a different client (${cName}) assigned during this timeframe`);
          return;
        }
      }

      this.gridSaving = true;
      try {
        await axios.post(`/hr/rota/${this.activeRota.id}/entries`, {
          shift_id:    this.addModal.shift.id,
          employee_id: this.addModal.employee_id,
          client_id:   this.addModal.client_id || undefined,
          work_date:   this.addModal.day.iso,
          start_time:  this.addModal.start_time || undefined,
          end_time:    this.addModal.end_time   || undefined,
          notes:       this.addModal.notes || undefined,
        });
        this.addModal.show = false;
        await this.refreshRota();
        this.toast.success('Staff added to schedule');
      } catch (err) {
        this.toast.error(err.response?.data?.message || 'Failed to add staff');
      } finally { this.gridSaving = false; }
    },

    // ── Entry Menu ────────────────────────────────────────────────────────────
    openEntryMenu(entry, shift, day) { this.entryMenu = { show: true, entry, shift, day }; },

    // ── Coverage bar helpers ──────────────────────────────────────────────────
    // Returns segments for each existing entry relative to shift window (0–100%)
    coverageSegments(shift, entries) {
      if (!shift?.start_time || !shift?.end_time) return [];
      const shiftStart = this.timeToMin(shift.start_time);
      const shiftDur   = this.durationMin(shift.start_time, shift.end_time);
      if (!shiftDur) return [];
      return entries.map(e => {
        const eStart = this.timeToMin(e.start_time || shift.start_time);
        const eDur   = this.durationMin(e.start_time || shift.start_time, e.end_time || shift.end_time);
        const left   = ((eStart - shiftStart + 1440) % 1440 / shiftDur) * 100;
        const width  = (eDur / shiftDur) * 100;
        const emp    = e.employee;
        const name   = emp ? this.shortName(emp) : '?';
        const t      = e.start_time || shift.start_time;
        const te     = e.end_time   || shift.end_time;
        return { empId: e.employee_id, leftPct: Math.max(0, left), widthPct: Math.min(100 - Math.max(0, left), width), label: `${name} (${t}–${te})` };
      });
    },
    previewSegStyle(shift, startTime, endTime) {
      if (!shift?.start_time || !startTime || !endTime) return {};
      const shiftStart = this.timeToMin(shift.start_time);
      const shiftDur   = this.durationMin(shift.start_time, shift.end_time);
      if (!shiftDur) return {};
      const left  = ((this.timeToMin(startTime) - shiftStart + 1440) % 1440 / shiftDur) * 100;
      const width = (this.durationMin(startTime, endTime) / shiftDur) * 100;
      return { left: Math.max(0, left) + '%', width: Math.min(100 - Math.max(0, left), width) + '%' };
    },
    coveragePct(shift, entries, previewStart, previewEnd) {
      if (!shift?.start_time || !shift?.end_time) return 0;
      const shiftDur = this.durationMin(shift.start_time, shift.end_time);
      if (!shiftDur) return 0;
      let covered = entries.reduce((s, e) => s + this.durationMin(e.start_time || shift.start_time, e.end_time || shift.end_time), 0);
      if (previewStart && previewEnd) covered += this.durationMin(previewStart, previewEnd);
      return Math.min(100, Math.round((covered / shiftDur) * 100));
    },
    calcDur(start, end) {
      if (!start || !end) return '';
      const m = this.durationMin(start, end);
      const h = Math.floor(m / 60), min = m % 60;
      return min ? `${h}h ${min}m` : `${h}h`;
    },

    // ── Cover ─────────────────────────────────────────────────────────────────
    openCover() {
      this.entryMenu.show = false;
      const t = Date.now();
      this.coverModal = {
        show:        true,
        entry:       this.entryMenu.entry,
        coverType:   'add',
        employee_id: null,
        notes:       '',
        start_time:  '',
        end_time:    '',
        searchKey:   t,
      };
    },
    onCoverEmployeeSelect(opt) { this.coverModal.employee_id = opt?.id ?? null; },
    async submitCover() {
      if (!this.coverModal.employee_id) { this.toast.error('Select a cover employee'); return; }
      this.gridSaving = true;
      try {
        if (this.coverModal.coverType === 'replace') {
          await axios.put(`/hr/rota/entries/${this.coverModal.entry.id}/replace`, {
            new_employee_id: this.coverModal.employee_id,
            start_time:      this.coverModal.start_time || undefined,
            end_time:        this.coverModal.end_time   || undefined,
            notes:           this.coverModal.notes || undefined,
          });
          this.coverModal.show = false;
          await this.refreshRota();
          this.toast.success('Employee replaced with cover');
        } else {
          // Add cover employee alongside the original — block only when hours are fully covered
          const shift    = this.gridShifts.find(s => s.id === this.coverModal.entry.shift_id);
          const workDate = this.coverModal.entry.work_date?.slice(0, 10);
          if (this.isShiftFull(shift, { iso: workDate })) {
            this.toast.error('Shift hours are fully covered for this day. Use "Replace" instead.');
            this.gridSaving = false;
            return;
          }
          await axios.post(`/hr/rota/${this.activeRota.id}/entries`, {
            shift_id:    this.coverModal.entry.shift_id,
            employee_id: this.coverModal.employee_id,
            work_date:   workDate,
            start_time:  this.coverModal.start_time || undefined,
            end_time:    this.coverModal.end_time   || undefined,
            notes:       this.coverModal.notes || undefined,
          });
          this.coverModal.show = false;
          await this.refreshRota();
          this.toast.success('Cover employee added to schedule');
        }
      } catch (err) {
        this.toast.error(err.response?.data?.message || 'Failed to add cover');
      } finally { this.gridSaving = false; }
    },

    // ── Replace ───────────────────────────────────────────────────────────────
    openReplace() {
      this.entryMenu.show = false;
      const t = Date.now();
      this.replaceModal = { show: true, entry: this.entryMenu.entry, new_employee_id: null, client_id: this.entryMenu.entry?.client_id ?? null, notes: '', searchKey: t, clientSearchKey: t + 1 };
    },
    onReplaceEmployeeSelect(opt) { this.replaceModal.new_employee_id = opt?.id ?? null; },
    onReplaceClientSelect(opt)   { this.replaceModal.client_id = opt?.id ?? null; },
    async submitReplace() {
      if (!this.replaceModal.new_employee_id) { this.toast.error('Select a replacement employee'); return; }
      this.gridSaving = true;
      try {
        await axios.put(`/hr/rota/entries/${this.replaceModal.entry.id}/replace`, {
          new_employee_id: this.replaceModal.new_employee_id,
          client_id:       this.replaceModal.client_id,
          notes:           this.replaceModal.notes || undefined,
        });
        this.replaceModal.show = false;
        await this.refreshRota();
        this.toast.success('Employee replaced');
      } catch (err) {
        this.toast.error(err.response?.data?.message || 'Failed to replace employee');
      } finally { this.gridSaving = false; }
    },

    // ── Remove ────────────────────────────────────────────────────────────────
    confirmRemove(entry, shift = null) {
      this.entryMenu.show = false;
      this.removeModal = { show: true, entry, shift: shift ?? this.entryMenu.shift };
    },
    async submitRemove() {
      const entry = this.removeModal.entry;
      this.gridSaving = true;
      try {
        if (entry._isAssignment) {
          await axios.delete(`/hr/shift/assignment/${entry.id}`);
        } else {
          await axios.delete(`/hr/rota/entries/${entry.id}`);
        }
        this.removeModal.show = false;
        await this.refreshRota();
        this.toast.success(entry._isAssignment ? 'Employee unassigned from shift' : 'Removed from schedule');
      } catch (err) {
        this.toast.error(err.response?.data?.message || 'Failed to remove');
      } finally { this.gridSaving = false; }
    },
  },
};
</script>

<style scoped>
/* ── Design tokens ─────────────────────────────────────────── */
.rota-page {
  --paper:       #F4F6F5;
  --surface:     #FFFFFF;
  --ink:         #16241F;
  --ink-muted:   #56685F;
  --ink-faint:   #8B978F;
  --line:        #DCE3DF;
  --accent:      #1F6F5C;
  --accent-soft: #E3F0EC;
  --accent-dark: #0F4136;
  --alert:       #B4432A;
  --alert-soft:  #F7E7E1;
  --amber:       #9C6C10;
  --amber-soft:  #F6EDD9;
  --radius:      10px;
  display: flex; flex-direction: column;
  font-family: 'Inter', sans-serif; color: var(--ink);
}
.mono { font-family: 'IBM Plex Mono', 'Courier New', monospace; }

/* ── Page header ────────────────────────────────────────────── */
.page-header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:18px; }
.page-title  { font-size:20px; font-weight:700; color:var(--ink); margin:0 0 3px; }
.page-sub    { font-size:12px; color:var(--ink-muted); margin:0; }

/* ── Buttons ─────────────────────────────────────────────────── */
button { font-family: 'Inter', sans-serif; cursor: pointer; }

.btn-primary {
  display:inline-flex; align-items:center; gap:6px;
  padding:8px 14px; background:var(--accent); color:#fff;
  border:1px solid var(--accent); border-radius:8px;
  font-size:13px; font-weight:500; transition:background .12s;
}
.btn-primary:hover    { background:var(--accent-dark); border-color:var(--accent-dark); }
.btn-primary:disabled { opacity:.6; cursor:not-allowed; }

.btn-outline {
  display:inline-flex; align-items:center; gap:5px;
  padding:6px 12px; background:var(--surface); color:var(--ink);
  border:1px solid var(--line); border-radius:8px;
  font-size:12px; font-weight:500; transition:all .12s;
}
.btn-outline:hover    { border-color:var(--accent); background:var(--accent-soft); }
.btn-outline:disabled { opacity:.6; cursor:not-allowed; }

.btn-icon-sm {
  width:30px; height:30px; background:none; border:1px solid var(--line);
  border-radius:7px; color:var(--ink-muted); font-size:13px;
  display:flex; align-items:center; justify-content:center; transition:all .12s;
}
.btn-icon-sm:hover           { border-color:var(--accent); background:var(--accent-soft); color:var(--accent); }
.btn-icon-sm--danger:hover   { border-color:var(--alert); background:var(--alert-soft); color:var(--alert); }

.btn-open {
  display:flex; align-items:center; gap:6px;
  padding:7px 13px; background:var(--accent-soft); color:var(--accent);
  border:1px solid var(--line); border-radius:8px;
  font-size:12px; font-weight:500; flex:1; justify-content:center; transition:background .12s;
}
.btn-open:hover { background:#d0e8e2; }

.btn-publish {
  display:inline-flex; align-items:center; gap:6px;
  padding:6px 12px; background:var(--accent-soft); color:var(--accent);
  border:1px solid var(--accent); border-radius:8px; font-size:12px; font-weight:500;
  transition:all .12s;
}
.btn-publish:hover { background:var(--accent); color:#fff; }
.btn-publish--live { background:var(--amber-soft); color:var(--amber); border-color:var(--amber); }
.btn-publish--live:hover { background:var(--amber); color:#fff; }
.btn-publish:disabled { opacity:.6; cursor:not-allowed; }

.btn-danger-solid {
  padding:8px 14px; background:var(--alert); color:#fff;
  border:1px solid var(--alert); border-radius:8px; font-size:13px; font-weight:500;
}
.btn-danger-solid:hover    { opacity:.9; }
.btn-danger-solid:disabled { opacity:.6; cursor:not-allowed; }

/* ── Filter bar ─────────────────────────────────────────────── */
.filter-bar  { display:flex; align-items:center; flex-wrap:wrap; gap:10px; margin-bottom:18px; }
.search-wrap { display:flex; align-items:center; gap:8px; background:var(--surface); border:1px solid var(--line); border-radius:8px; padding:0 10px; height:36px; flex:1; min-width:160px; }
.search-ico  { color:var(--ink-faint); font-size:12px; flex-shrink:0; }
.search-input{ border:none; background:none; outline:none; font-size:13px; color:var(--ink); width:100%; }
.search-input::placeholder { color:var(--ink-faint); }
.filter-sel  { height:36px; padding:0 10px; border:1px solid var(--line); border-radius:8px; background:var(--surface); font-size:13px; color:var(--ink); outline:none; }
.filter-count{ font-size:12px; color:var(--ink-faint); margin-left:auto; }

/* ── Rota cards ─────────────────────────────────────────────── */
.rota-cards  { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:14px; }
.rota-card   { background:var(--surface); border:1px solid var(--line); border-radius:var(--radius); padding:16px; display:flex; flex-direction:column; gap:12px; transition:box-shadow .15s; }
.rota-card:hover { box-shadow:0 4px 14px rgba(0,0,0,.07); }
.rota-card-head  { display:flex; align-items:center; gap:10px; }
.rota-icon-wrap  { width:38px; height:38px; border-radius:8px; background:var(--accent-soft); color:var(--accent); display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
.rota-name-block { flex:1; min-width:0; }
.rota-name   { font-size:14px; font-weight:600; color:var(--ink); margin:0 0 2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.rota-range  { font-size:11px; color:var(--ink-muted); margin:0; }
.rota-locations { display:flex; align-items:center; gap:4px; margin-top:3px; font-size:11px; color:var(--accent-dark); font-weight:500; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.rota-loc-icon  { font-size:9px; flex-shrink:0; color:var(--accent); }
.rota-stats  { display:flex; gap:14px; background:var(--paper); border-radius:8px; padding:9px 12px; }
.rota-stat   { display:flex; flex-direction:column; gap:1px; }
.stat-val    { font-size:17px; font-weight:700; color:var(--ink); line-height:1; }
.stat-lbl    { font-size:10px; text-transform:uppercase; letter-spacing:.05em; color:var(--ink-muted); font-weight:600; }
.rota-card-foot { display:flex; align-items:center; gap:6px; }

/* ── Badges ──────────────────────────────────────────────────── */
.badge       { display:inline-flex; align-items:center; padding:3px 9px; border-radius:20px; font-size:10px; font-weight:700; white-space:nowrap; flex-shrink:0; }
.badge--pub  { background:var(--accent-soft); color:var(--accent-dark); }
.badge--draft{ background:var(--amber-soft);  color:var(--amber); border:1px solid #e0c87a; }

/* ── Empty / skeleton ───────────────────────────────────────── */
.empty-state { display:flex; flex-direction:column; align-items:center; gap:14px; padding:70px 20px; color:var(--ink-muted); }
.empty-icon  { font-size:48px; opacity:.25; }
.empty-state p { font-size:14px; margin:0; }
.sk-card { border-radius:var(--radius); background:linear-gradient(90deg,var(--paper) 25%,var(--line) 50%,var(--paper) 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; height:175px; }
@keyframes shimmer { 0%{background-position:200% 0}100%{background-position:-200% 0} }

/* ── Modal overlay (shared by all pop-up modals) ────────────── */
.modal-overlay  { position:fixed; inset:0; background:rgba(0,0,0,.4); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; }

/* ── Schedule page ──────────────────────────────────────────── */
.sched-page     { display:flex; flex-direction:column; height:100%; background:var(--surface); }

.sched-head     { display:flex; align-items:center; gap:12px; padding:13px 18px; border-bottom:1px solid var(--line); flex-shrink:0; flex-wrap:wrap; background:var(--paper); }
.sched-head-info { flex:1; min-width:0; }
.sched-title    { font-size:15px; font-weight:700; color:var(--ink); margin:0 0 2px; }
.sched-range    { font-size:11px; color:var(--ink-muted); margin:0; }
.sched-head-actions { display:flex; align-items:center; gap:6px; flex-wrap:wrap; margin-left:auto; }
.btn-back       { display:inline-flex; align-items:center; gap:6px; padding:6px 12px; border:1px solid var(--line); border-radius:8px; background:var(--paper); color:var(--ink-muted); font-size:13px; font-weight:600; cursor:pointer; white-space:nowrap; transition:all .12s; flex-shrink:0; }
.btn-back:hover { border-color:var(--accent); color:var(--accent); background:var(--accent-soft); }

/* ── Rules bar ───────────────────────────────────────────────── */
.rules-bar {
  display:flex; align-items:center; gap:14px; flex-wrap:wrap;
  padding:8px 18px; border-bottom:1px solid var(--line); background:var(--paper);
  font-size:12px; color:var(--ink-muted); flex-shrink:0;
}
.rules-label { font-weight:600; color:var(--ink-muted); white-space:nowrap; }
.rule-field  { display:flex; align-items:center; gap:5px; }
.rule-input  { width:52px; padding:3px 6px; border:1px solid var(--line); border-radius:6px; font-size:12px; font-family:'Inter',sans-serif; background:var(--surface); color:var(--ink); }
.rules-hint  { font-size:11px; color:var(--ink-faint); margin-left:auto; }

/* ── Schedule client filter bar ─────────────────────────────── */
.sched-filter-bar    { display:flex; align-items:center; flex-wrap:wrap; gap:8px; padding:8px 18px; border-bottom:1px solid var(--line); background:var(--surface); flex-shrink:0; }
.sched-filter-icon   { color:var(--ink-faint); font-size:12px; flex-shrink:0; }
.sched-filter-label  { font-size:11px; font-weight:600; color:var(--ink-muted); white-space:nowrap; flex-shrink:0; }
.sched-filter-search { width:220px; flex-shrink:0; }
.sched-filter-clear  { display:inline-flex; align-items:center; gap:5px; height:30px; padding:0 10px; border:1px solid var(--accent); border-radius:20px; background:var(--accent-soft); color:var(--accent); font-size:11px; font-weight:600; cursor:pointer; white-space:nowrap; transition:all .12s; }
.sched-filter-clear:hover { background:var(--accent); color:#fff; }

.sched-load  { padding:20px; }
.sched-body  { flex:1; overflow:auto; display:flex; flex-direction:column; }

/* ── Violations banner ──────────────────────────────────────── */
.violations-box        { padding:8px 18px; font-size:12.5px; flex-shrink:0; border-bottom:1px solid var(--line); }
.violations-header     { display:flex; align-items:center; justify-content:space-between; cursor:pointer; user-select:none; gap:8px; }
.violations-chevron    { font-size:11px; opacity:.7; }
.violations-box ul     { margin:6px 0 4px; padding-left:18px; }
.violations-box li     { margin-bottom:2px; }
.violations-detail     { margin:6px 0 4px; font-size:12px; opacity:.85; }
.violations-box--warn  { background:var(--alert-soft); color:var(--alert); }
.violations-box--ok    { background:var(--accent-soft); color:var(--accent-dark); }

/* ── Grid ────────────────────────────────────────────────────── */
.grid-wrap { overflow-x:auto; flex-shrink:0; }
.rota-table { width:100%; border-collapse:collapse; font-size:13px; table-layout:fixed; min-width:700px; }

.th-shift { width:130px; min-width:110px; background:var(--paper); border-bottom:1px solid var(--line); border-right:2px solid var(--accent); position:sticky; left:0; z-index:10; }
.th-day   { text-align:left; background:var(--paper); border-bottom:1px solid var(--line); border-right:1px solid var(--line); padding:8px 10px; min-width:120px; }
.th-day--today   { background:var(--accent-soft); }
.th-day--weekend { color:var(--ink-muted); }
.th-day-name { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--ink-muted); }
.th-day--today .th-day-name { color:var(--accent-dark); }
.th-day-date { font-size:12px; font-weight:600; color:var(--ink); }

.shift-row        { border-bottom:1px solid var(--line); }
.shift-row:last-child { border-bottom:none; }

.td-shift { background:var(--paper); border-right:2px solid var(--accent); padding:10px; vertical-align:top; position:sticky; left:0; z-index:5; }
.shift-name   { font-size:12px; font-weight:700; color:var(--ink); }
.shift-time   { font-size:10px; color:var(--ink-muted); margin-top:1px; }
.shift-client { display:flex; align-items:center; gap:4px; font-size:10px; color:var(--accent); margin-top:3px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.btn-bulk-assign { display:flex; align-items:center; gap:4px; margin-top:6px; padding:3px 7px; font-size:10px; font-weight:600; border:1px solid var(--accent); border-radius:4px; color:var(--accent); background:var(--accent-soft); cursor:pointer; white-space:nowrap; }
.btn-bulk-assign:hover { background:var(--accent); color:#fff; }

/* ── Bulk Assign Modal ─────────────────────────── */
.bulk-modal      { width:480px; max-width:96vw; }
.bulk-shift-tag  { font-size:12px; color:var(--ink-muted); margin-top:2px; }
.bulk-client-tag { color:var(--accent); font-weight:600; }
.bulk-col-head   { display:flex; align-items:center; justify-content:space-between; margin-bottom:6px; }
.bulk-day-shortcuts { display:flex; gap:4px; }
.bulk-shortcut   { padding:2px 8px; border:1px solid var(--line); border-radius:4px; font-size:11px; cursor:pointer; background:var(--paper); }
.bulk-shortcut:hover { border-color:var(--accent); color:var(--accent); }
.bulk-days       { display:flex; flex-wrap:wrap; gap:6px; }
.bulk-day        { display:flex; flex-direction:column; align-items:center; gap:1px; padding:6px 8px; border:1px solid var(--line); border-radius:6px; cursor:pointer; min-width:44px; }
.bulk-day:not(.bulk-day--past):hover, .bulk-day--checked { background:var(--accent-soft); border-color:var(--accent); }
.bulk-day--weekend { border-style:dashed; }
.bulk-day--past  { opacity:.45; cursor:default; }
.bulk-day input[type="checkbox"] { display:none; }
.bulk-day-label  { display:flex; flex-direction:column; align-items:center; pointer-events:none; }
.bulk-day-dow    { font-size:10px; font-weight:700; color:var(--ink-muted); text-transform:uppercase; }
.bulk-day-date   { font-size:12px; font-weight:600; color:var(--ink); }
.bulk-preview    { font-size:13px; color:var(--ink-muted); margin-right:auto; }
.bulk-result     { margin:0 16px; padding:8px 12px; border-radius:6px; font-size:13px; display:flex; align-items:center; gap:8px; }
.bulk-result--ok   { background:#e8f5ee; color:#1a7042; }
.bulk-result--warn { background:#fff8e1; color:#856600; }
.bulk-skip-hint  { font-size:11px; opacity:.8; }

.td-cell          { padding:8px 10px; vertical-align:top; border-right:1px solid var(--line); min-height:70px; }
.td-cell--today   { background:#f0faf6; }
.td-cell--weekend { background:var(--paper); }
.td-cell--past    { background:#f7f7f7; opacity:.75; }
.td-cell--short   { background:var(--amber-soft); }

.cell-need        { font-size:10px; color:var(--ink-faint); margin-bottom:4px; }
.cell-need--short { color:var(--amber); font-weight:600; }

/* ── Chips ───────────────────────────────────────────────────── */
.chip-wrap { display:flex; flex-wrap:wrap; gap:3px; margin-bottom:4px; }
.chip      { display:inline-flex; align-items:center; gap:4px; padding:3px 6px 3px 8px; border-radius:20px; font-size:11px; color:#fff; font-weight:500; cursor:pointer; transition:opacity .1s; }
.chip:hover { opacity:.85; }
.chip--conflict      { box-shadow: 0 0 0 2px var(--alert) inset; }
.chip--assignment    { opacity:.85; outline: 2px dashed rgba(255,255,255,.55); outline-offset:-2px; }
.chip--past          { cursor: default; opacity:.6; filter:grayscale(.4); }
.chip--on-leave      { outline: 2px solid #e67e22; outline-offset:-2px; }
.chip--leave-pending { outline: 2px dashed #e67e22; outline-offset:-2px; }
.chip-leave-dot         { width:7px; height:7px; border-radius:50%; background:#e67e22; flex-shrink:0; margin-right:1px; }
.chip-leave-dot--pending { background:transparent; border:2px solid #e67e22; }
.assign-source-note { font-size:11px; color:var(--ink-faint); margin:0 0 10px; font-style:italic; }
.chip-x    { cursor:pointer; opacity:.75; font-weight:700; font-size:12px; }
.chip-x:hover { opacity:1; }

.cell-add       { display:inline-flex; align-items:center; justify-content:center; width:22px; height:22px; border:1.5px dashed var(--line); border-radius:6px; background:none; color:var(--ink-faint); font-size:10px; cursor:pointer; transition:all .12s; }
.cell-add:hover { border-color:var(--accent); color:var(--accent); background:var(--accent-soft); }
.cell-add--full { border-style:solid; border-color:var(--accent); color:var(--accent); background:var(--accent-soft); cursor:default; opacity:.7; }
.cell-add--full:hover { border-color:var(--accent); color:var(--accent); background:var(--accent-soft); }

.grid-empty { text-align:center; padding:40px 20px; color:var(--ink-muted); font-size:13px; }
.link { color:var(--accent); text-decoration:underline; }

/* ── Hours ledger ────────────────────────────────────────────── */
.ledger       { border-top:1px solid var(--line); flex-shrink:0; }
.ledger-title { font-size:13px; font-weight:700; color:var(--ink); padding:12px 18px 8px; margin:0; }
.ledger-table { width:100%; border-collapse:collapse; font-size:12px; }
.ledger-table th { text-align:left; font-size:10px; text-transform:uppercase; letter-spacing:.04em; color:var(--ink-muted); padding:7px 14px; background:var(--paper); border-bottom:1px solid var(--line); }
.ledger-table td { padding:8px 14px; border-bottom:1px solid var(--line); vertical-align:middle; }
.ledger-table tr:last-child td { border-bottom:none; }
.swatch      { display:inline-block; width:9px; height:9px; border-radius:50%; margin-right:7px; vertical-align:middle; }
.ledger-name { font-size:12px; font-weight:500; }
.text-warn   { color:var(--alert); }

/* Gauge (Section 7) */
.gauge        { position:relative; width:140px; height:14px; }
.gauge-track  { position:absolute; top:6px; left:0; right:0; height:2px; background:var(--line); }
.gauge-fill   { position:absolute; top:5px; left:0; height:4px; border-radius:2px; transition:width .3s; }
.gauge-marker { position:absolute; top:1px; width:2px; height:12px; background:var(--ink); }

/* ── Modals ─────────────────────────────────────────────────── */
.modal-box     { background:var(--surface); border-radius:var(--radius); width:100%; max-width:500px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-box--sm { max-width:380px; }
.modal-head    { display:flex; align-items:center; justify-content:space-between; padding:15px 20px; border-bottom:1px solid var(--line); background:var(--paper); border-radius:var(--radius) var(--radius) 0 0; }
.modal-title   { font-size:14px; font-weight:700; color:var(--ink); margin:0; }
.modal-body    { padding:18px 20px; display:flex; flex-direction:column; gap:12px; }
.modal-body--search { padding-bottom:300px; }
.modal-foot    { display:flex; justify-content:flex-end; gap:8px; padding:13px 20px; border-top:1px solid var(--line); }
.modal-foot button { padding:7px 14px; border-radius:8px; font-size:13px; font-weight:500; border:1px solid var(--line); background:var(--surface); color:var(--ink); }
.modal-foot button:hover { border-color:var(--accent); background:var(--accent-soft); }

/* ── Fields ──────────────────────────────────────────────────── */
.field       { display:flex; flex-direction:column; gap:4px; }
.field-row   { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
.field-lbl   { font-size:10.5px; color:var(--ink-muted); font-weight:600; text-transform:uppercase; letter-spacing:.04em; }
.field input, .field select { padding:7px 9px; border:1px solid var(--line); border-radius:7px; font-size:13px; color:var(--ink); background:var(--surface); font-family:'Inter',sans-serif; outline:none; }
.field input:focus, .field select:focus { border-color:var(--accent); }
.opt { color:var(--ink-faint); font-weight:400; text-transform:none; }
.range-btns button { padding:5px 11px; background:var(--paper); color:var(--ink); border:1px solid var(--line); border-radius:7px; font-size:12px; margin-right:6px; }
.range-btns button:hover { border-color:var(--accent); background:var(--accent-soft); }
.field-hint { font-size:11px; color:var(--ink-faint); }

/* ── Location chips (modal + filter bar) ─────────────────────── */
.multi-sel-box         { display:flex; flex-direction:column; border:1px solid var(--line); border-radius:8px; overflow:hidden; max-height:180px; overflow-y:auto; }
.multi-sel-opt         { display:flex; align-items:center; gap:10px; padding:8px 12px; cursor:pointer; transition:background .1s; border-bottom:1px solid var(--line); }
.multi-sel-opt:last-child { border-bottom:none; }
.multi-sel-opt:hover   { background:var(--paper); }
.multi-sel-opt--on     { background:var(--accent-soft); }
.multi-sel-opt--on .multi-sel-name { color:var(--accent-dark); font-weight:600; }
.multi-sel-chk         { accent-color:var(--accent); width:15px; height:15px; flex-shrink:0; cursor:pointer; }
.multi-sel-name        { font-size:13px; color:var(--ink); }

.sched-filter-loc-chips  { display:flex; flex-wrap:wrap; gap:5px; }
.sched-loc-chip          { display:inline-flex; align-items:center; padding:3px 10px; border:1px solid var(--line); border-radius:20px; background:var(--paper); color:var(--ink-muted); font-size:11px; font-weight:500; cursor:pointer; transition:all .12s; white-space:nowrap; }
.sched-loc-chip:hover    { border-color:var(--accent); color:var(--accent); }
.sched-loc-chip--on      { border-color:var(--accent); background:var(--accent-soft); color:var(--accent-dark); font-weight:600; }
.sched-loc-clear         { display:inline-flex; align-items:center; gap:4px; padding:3px 10px; border:1px solid #fca5a5; border-radius:20px; background:#fef2f2; color:#dc2626; font-size:11px; font-weight:600; cursor:pointer; white-space:nowrap; }

/* ── Slot / replace notes ────────────────────────────────────── */
.slot-info { display:flex; align-items:center; gap:7px; flex-wrap:wrap; padding:8px 11px; background:var(--accent-soft); border-radius:8px; font-size:12.5px; color:var(--accent-dark); }
.slot-info--plain { background:var(--paper); color:var(--ink-muted); padding:6px 11px; border-radius:7px; margin:0; font-size:12.5px; }
.replace-note { font-size:13px; color:var(--ink-muted); background:var(--paper); border-radius:8px; padding:9px 12px; margin:0; line-height:1.6; }

/* ── Entry actions ───────────────────────────────────────────── */
.entry-actions { display:flex; flex-direction:column; gap:8px; }
.entry-btn { width:100%; display:flex; align-items:center; gap:10px; padding:10px 13px; border-radius:9px; border:1px solid var(--line); font-size:13px; font-weight:500; cursor:pointer; transition:all .12s; background:var(--surface); color:var(--ink); }
.entry-btn--cover:hover   { border-color:#5B21B6; background:#f3e8ff; color:#5B21B6; }
.entry-btn--replace:hover { border-color:var(--accent); background:var(--accent-soft); color:var(--accent); }
.entry-btn--remove:hover  { border-color:var(--alert);  background:var(--alert-soft);  color:var(--alert); }

/* ── Chip time label ─────────────────────────────────────────── */
.chip-inner { display:flex; flex-direction:column; gap:1px; }
.chip-name  { line-height:1.2; }
.chip-time  { font-size:9px; opacity:.85; font-family:'IBM Plex Mono','Courier New',monospace; line-height:1; }

/* ── Time split (hours allocation) ──────────────────────────── */
.time-split       { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.time-split-field { display:flex; flex-direction:column; gap:2px; }
.time-split-lbl   { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:var(--ink-muted); }
.time-split-input { padding:5px 8px; border:1px solid var(--line); border-radius:7px; font-size:13px; color:var(--ink); background:var(--surface); font-family:'Inter',sans-serif; outline:none; }
.time-split-input:focus { border-color:var(--accent); }
.time-split-sep   { color:var(--ink-faint); font-size:14px; margin-top:14px; }
.time-split-dur   { font-size:12px; font-weight:700; color:var(--accent); background:var(--accent-soft); padding:3px 9px; border-radius:20px; margin-top:14px; white-space:nowrap; }
.time-split-clear { width:26px; height:26px; border:1px solid var(--line); border-radius:6px; background:none; color:var(--ink-faint); font-size:11px; display:flex; align-items:center; justify-content:center; cursor:pointer; margin-top:14px; }
.time-split-clear:hover { border-color:var(--alert); color:var(--alert); }

/* ── Coverage bar ────────────────────────────────────────────── */
.cov-bar-wrap { display:flex; flex-direction:column; gap:5px; }
.cov-bar      { position:relative; height:16px; background:var(--paper); border:1px solid var(--line); border-radius:6px; overflow:hidden; }
.cov-seg      { position:absolute; top:0; bottom:0; opacity:.8; border-radius:3px; transition:width .2s; }
.cov-seg--preview { background:rgba(0,74,173,.35) !important; border:1.5px dashed #004AAD; opacity:1; }
.cov-legend   { display:flex; flex-wrap:wrap; gap:6px; }
.cov-legend-item { display:inline-flex; align-items:center; gap:4px; font-size:11px; color:var(--ink-muted); }
.cov-dot      { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
.cov-pct      { font-size:11px; font-weight:700; color:var(--accent); }

/* ── Cover modal ────────────────────────────────────────────── */
.cover-context {
  display:flex; flex-direction:column; gap:6px;
  background:var(--paper); border:1px solid var(--line);
  border-radius:9px; padding:11px 14px; font-size:12.5px;
}
.cover-context-row  { display:flex; align-items:center; gap:7px; flex-wrap:wrap; }
.cover-unavail-label{ font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:var(--ink-muted); }
.cover-chip { display:inline-flex; align-items:center; padding:3px 10px; border-radius:20px; font-size:11px; color:#fff; font-weight:600; }
.cover-slot { color:var(--accent-dark); }

.cover-type-opts { display:flex; flex-direction:column; gap:7px; }
.cover-type-opt  {
  display:flex; align-items:center; gap:10px;
  padding:10px 13px; border:1px solid var(--line); border-radius:9px;
  cursor:pointer; transition:all .12s; background:var(--surface);
}
.cover-type-opt > svg { font-size:16px; color:var(--ink-faint); flex-shrink:0; }
.cover-type-opt div   { display:flex; flex-direction:column; gap:1px; }
.cover-type-opt div strong { font-size:13px; font-weight:600; color:var(--ink); }
.cover-type-opt div span   { font-size:11px; color:var(--ink-muted); }
.cover-type-opt--active { border-color:#5B21B6; background:#f3e8ff; }
.cover-type-opt--active > svg { color:#5B21B6; }

/* ── Roll forward ────────────────────────────────────────────── */
.roll-opts { display:flex; flex-direction:column; gap:7px; }
.roll-opt  { display:flex; align-items:center; gap:10px; padding:12px 14px; border:1px solid var(--line); border-radius:9px; cursor:pointer; transition:all .12s; }
.roll-opt > svg { font-size:18px; color:var(--ink-faint); flex-shrink:0; }
.roll-opt div   { display:flex; flex-direction:column; gap:2px; }
.roll-opt div strong { font-size:13px; font-weight:600; color:var(--ink); }
.roll-opt div span   { font-size:11px; color:var(--ink-muted); }
.roll-opt--active    { border-color:var(--accent); background:var(--accent-soft); }
.roll-opt--active > svg { color:var(--accent); }

/* ── Shift type filter ───────────────────────────────────────── */
.sched-filter-type  { display:flex; align-items:center; gap:8px; }
.sched-filter-emp   { display:flex; align-items:center; gap:8px; }
.sched-type-sel     { height:32px; padding:0 10px; border:1px solid var(--line); border-radius:8px; font-size:13px; background:#fff; color:var(--ink); outline:none; }
.sched-type-sel:focus { border-color:var(--accent); }

/* ── Export column picker ─────────────────────────────── */
.export-wrap { position:relative; display:inline-flex; }
.export-menu { position:absolute; top:calc(100% + 4px); right:0; background:#fff; border:1px solid #dde5f0; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); z-index:200; min-width:160px; padding:6px; }
.export-item { display:flex; align-items:center; gap:8px; width:100%; padding:8px 12px; border:none; background:none; font-size:13px; color:#1a2b45; cursor:pointer; border-radius:6px; }
.export-item:hover { background:#f0f7ff; color:#004AAD; }
.col-picker-backdrop { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1200; display:flex; align-items:center; justify-content:center; padding:16px; }
.col-picker-modal  { background:#fff; border-radius:16px; width:100%; max-width:520px; max-height:90vh; overflow-y:auto; box-shadow:0 24px 64px rgba(0,0,0,.18); }
.col-picker-header { display:flex; align-items:flex-start; justify-content:space-between; padding:20px 22px 0; }
.col-picker-title  { font-size:15px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.col-picker-sub    { font-size:12px; color:#7a90b0; margin:0; }
.col-picker-close  { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; padding:4px 6px; flex-shrink:0; }
.col-picker-actions { display:flex; align-items:center; gap:8px; padding:14px 22px 0; }
.col-picker-link   { background:none; border:none; cursor:pointer; color:#004AAD; font-size:12px; font-weight:600; padding:0; }
.col-picker-sep    { color:#dde5f0; }
.col-picker-count  { margin-left:auto; font-size:12px; color:#7a90b0; }
.col-picker-grid   { display:grid; grid-template-columns:1fr 1fr; gap:4px; padding:14px 22px; }
.col-picker-item   { display:flex; align-items:center; gap:8px; padding:8px 10px; border-radius:8px; cursor:pointer; border:1px solid #f0f4f8; }
.col-picker-item:hover { background:#f0f7ff; border-color:#c7d8ef; }
.col-picker-check  { cursor:pointer; accent-color:#004AAD; width:15px; height:15px; flex-shrink:0; }
.col-picker-label  { font-size:13px; color:#1a2b45; font-weight:500; }
.col-picker-footer { display:flex; justify-content:flex-end; gap:8px; padding:14px 22px 20px; border-top:1px solid #f0f4f8; margin-top:8px; }
.btn-outline.btn-secondary { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; }
.btn-primary-solid { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary-solid:disabled { opacity:.6; cursor:not-allowed; }
.fade-enter-active,.fade-leave-active { transition:opacity .18s; }
.fade-enter,.fade-leave-to { opacity:0; }

/* ── Per-page + Pagination ─────────────────────────────── */
.per-page-wrap    { display:flex; align-items:center; gap:6px; }
.per-page-label   { font-size:12px; color:#7a90b0; white-space:nowrap; }
.per-page-select  { height:34px; padding:0 10px; border:1px solid var(--line); border-radius:8px; font-size:13px; color:var(--ink); background:#fff; outline:none; }
.pagination-bar   { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; padding:14px 4px; margin-top:4px; }
.pagination-info  { font-size:13px; color:var(--ink-muted); }
.pagination-controls { display:flex; align-items:center; gap:4px; }
.pg-btn  { background:#fff; border:1px solid var(--line); border-radius:6px; width:32px; height:32px; font-size:13px; cursor:pointer; display:flex; align-items:center; justify-content:center; color:var(--ink); }
.pg-btn:hover:not(:disabled) { background:var(--accent-soft); border-color:var(--accent); color:var(--accent); }
.pg-btn:disabled { opacity:.4; cursor:not-allowed; }
.pg-pages { display:flex; align-items:center; gap:2px; }
.pg-num   { background:#fff; border:1px solid var(--line); border-radius:6px; width:32px; height:32px; font-size:13px; cursor:pointer; display:flex; align-items:center; justify-content:center; color:var(--ink); }
.pg-num:hover    { background:var(--accent-soft); border-color:var(--accent); color:var(--accent); }
.pg-num--active  { background:var(--accent); border-color:var(--accent); color:#fff; font-weight:700; }
</style>
